import axios from "axios";
import axiosRetry from "axios-retry";
import moment from "moment-timezone";
import { audit, sendToMobileApp } from "../utils/utils";
import { publishOnlineEvidence } from "../utils/onlineEvidence";
import { buildChatSearchFilters } from "../utils/chatSearch";
import { mergeStockIntoCatalog } from "../utils/peptideStock";
import { publish } from "../utils/events";
import { isServerDownError } from "../utils/serverErrors";
import { stripTransientUploadFlags } from "../utils/uploadTracking";
import { stampCreationLocation, resolveCreationLocationId, getEncounterLocationContext } from "../utils/encounterLocation";
import { DOCUMENTS_OVERDUE_EVENT, isDocumentsOverdueOutput, makeDocumentsOverdueError } from "../utils/requiredDocsLock";
import { isProductRestrictedOutput, makeProductRestrictedError } from "../utils/productRestrictions";
import { omitCustomNotesFromOrg, omitCustomNotesFromProfile } from "../utils/customNotesPersist";

// Builds the `status` query-string fragment (e.g. "archived,completed") from the
// same boolean-flag `filters` shape callers already pass to getChats. Shared by
// getChats and Analytics.js's fetchStats -- whose per-doctor scoped requests
// must query the identical status set as the main list, or the mapping silently
// drifts -- so there is exactly one place this derivation can go stale.
export const buildStatusFilter = (filters = {}) => {
  const statusFilter = [];
  if (filters.viewActive) statusFilter.push("active");
  if (filters.viewArchived) statusFilter.push("archived");
  if (filters.viewCompleted) statusFilter.push("completed");
  if (filters.viewInvalid) statusFilter.push("invalid");
  return statusFilter.join(",");
};

export const getBaseURL = () => {
  let brand = sessionStorage.getItem("brand");
  let appUrl = process.env.REACT_APP_ENDPOINT;
  if (brand === "healcore") appUrl = process.env.REACT_APP_ENDPOINT_HEALCORE;
  if (brand === "rpg") appUrl = process.env.REACT_APP_ENDPOINT_RPG;
  if (brand === "shoreline") appUrl = process.env.REACT_APP_ENDPOINT_SHORELINE;
  if (brand === "freehealthai") appUrl = process.env.REACT_APP_ENDPOINT_FREEHEALTHAI;
  if (brand === "gtm") appUrl = process.env.REACT_APP_ENDPOINT_GTM;
  if (brand === "optimalcaregrp") appUrl = process.env.REACT_APP_ENDPOINT_OPTIMALCAREGRP;
  if (brand === "orders") appUrl = process.env.REACT_APP_ENDPOINT_ORDERS;
  if (brand === "alphabiologix") appUrl = process.env.REACT_APP_ENDPOINT_ALPHABIOLOGIX;
  return appUrl;
};

// Tenant-server allowlist for the `?server=` referral-link param. Only known
// values route the signup call to a non-default backend; anything else falls
// through to the default trial endpoint so a hostile URL can't force the
// signup at an arbitrary host. CRA inlines `process.env.REACT_APP_*` at build
// time, so each entry must be a literal — extend by adding a new line plus the
// matching `REACT_APP_ENDPOINT_<NAME>` env var.
const SERVER_ENDPOINTS = {
  app: process.env.REACT_APP_ENDPOINT,
  orders: process.env.REACT_APP_ENDPOINT_ORDERS,
  alphabiologix: process.env.REACT_APP_ENDPOINT_ALPHABIOLOGIX,
  gtm: process.env.REACT_APP_ENDPOINT_GTM,
  shoreline: process.env.REACT_APP_ENDPOINT_SHORELINE,
  rpg: process.env.REACT_APP_ENDPOINT_RPG,
  healcore: process.env.REACT_APP_ENDPOINT_HEALCORE,
  optimalcaregrp: process.env.REACT_APP_ENDPOINT_OPTIMALCAREGRP,
  freehealthai: process.env.REACT_APP_ENDPOINT_FREEHEALTHAI,
};

// Outstanding intake links created before `?server=` was added can still be
// routed safely from the exact Lucas frontend host that issued them. Keep this
// exact-match only: substring/suffix matching would let an unrelated hostname
// select a tenant backend.
const LEGACY_PUBLIC_SERVER_BY_HOST = {
  // Generic mobile hosts can serve providers connected to different brains,
  // so they are intentionally absent. A pre-server link from one of those
  // hosts is ambiguous and must be resent rather than guessed across tenants.
  localhost: "app",
  "127.0.0.1": "app",
  "orders-app.lucashealth.ai": "orders",
  "staging-orders-app.lucashealth.ai": "orders",
  "alphabiologix-app.lucashealth.ai": "alphabiologix",
  "staging-alphabiologix-app.lucashealth.ai": "alphabiologix",
  "gtm-app.lucashealth.ai": "gtm",
  "staging-gtm-app.lucashealth.ai": "gtm",
  "shoreline-app.lucashealth.ai": "shoreline",
  "staging-shoreline-app.lucashealth.ai": "shoreline",
  "rpg-app.lucashealth.ai": "rpg",
  "staging-rpg-app.lucashealth.ai": "rpg",
  "healcore-app.lucashealth.ai": "healcore",
  "staging-healcore-app.lucashealth.ai": "healcore",
  "optimalcaregrp-app.lucashealth.ai": "optimalcaregrp",
  "staging-optimalcaregrp-app.lucashealth.ai": "optimalcaregrp",
  "freehealthai-app.lucashealth.ai": "freehealthai",
  "staging-freehealthai-app.lucashealth.ai": "freehealthai",
};

const resolveServerEndpoint = (server) => {
  if (!server) return null;
  return SERVER_ENDPOINTS[server.toLowerCase()] || null;
};

const normalizeServerEndpoint = (endpoint) =>
  String(endpoint || "")
    .trim()
    .toLowerCase()
    .replace(/\/+$/, "");

// Reverse the same endpoint allowlist used by public intake links. Invite
// sending resolves the request's in-memory Axios destination through this map;
// shared browser storage may corroborate that destination but never select it.
// This remains reliable when the backend exposes a legacy/non-public context URL.
export const resolveClinicalProfileServerFromEndpoint = (endpoint) => {
  const normalized = normalizeServerEndpoint(endpoint);
  if (!normalized) return null;
  const match = Object.entries(SERVER_ENDPOINTS).find(
    ([, configuredEndpoint]) => configuredEndpoint && normalizeServerEndpoint(configuredEndpoint) === normalized
  );
  return match?.[0] || null;
};

export const resolveLegacyClinicalProfileServer = (hostname) =>
  LEGACY_PUBLIC_SERVER_BY_HOST[
    String(hostname || "")
      .trim()
      .toLowerCase()
  ] || null;

export const isKnownServerKey = (server) => !!resolveServerEndpoint(server);

// Public Clinical Profile links must never inherit localStorage.hostName from
// a provider or another tenant that previously used this browser. New links
// carry an allowlisted server key; legacy links use an exact, trusted frontend
// host mapping. An explicit but unknown key is rejected rather than silently
// sending the bearer credential to a different tenant. Passing an absolute URL
// also bypasses the authenticated axios instance's saved baseURL.
// Returns the resolved key alongside the endpoint: the backend cannot always
// name itself (brain:baseUrl is unset on some brains, and invites predating the
// stored `server` field carry only a browser origin), so the portal reports the
// key it just used to pick this endpoint. It is a hint the backend may ignore,
// never an instruction — see resolveServerKey in clinicalProfileInviteRouting.js.
const resolvePublicClinicalProfileEndpoint = (server) => {
  if (server) {
    const endpoint = resolveServerEndpoint(server);
    if (!endpoint) throw new TypeError("clinical_profile_server_invalid");
    return { endpoint, server: String(server).toLowerCase() };
  }
  const legacyServer = resolveLegacyClinicalProfileServer(typeof window !== "undefined" ? window.location.hostname : "");
  const endpoint = resolveServerEndpoint(legacyServer);
  if (!endpoint) throw new TypeError("clinical_profile_server_missing");
  return { endpoint, server: legacyServer };
};

// Above this base64 length (~4MB) a recording is uploaded in pieces instead of
// one JSON body; each piece is ~700KB so a dropped connection only loses one
// small request, not the whole recording.
export const TRANSCRIPT_CHUNK_THRESHOLD = 4 * 1024 * 1024;
const TRANSCRIPT_CHUNK_SIZE = 700 * 1024;
// Small recording uploads (a ~700KB chunk, a ~10s segment) either finish in
// seconds or are stuck; the 5-minute axios default turns a half-open socket
// into a 5-minute stall per attempt. Aborting fast never loses audio: both
// are write-ahead persisted locally. Retry semantics differ by path — a
// chunk failure requeues the WHOLE recording via the durable resync queue
// (re-uploads are idempotent: deterministic server fileNames); a segment
// upload is one-shot best-effort (the server copy is a recovery backstop;
// the local IndexedDB copy remains the transcript source).
// 60s, not 30: a ~700KB chunk at rural upload speeds (~128 kbps effective)
// legitimately takes ~45s — timing out demotes that recording to a WORSE
// 14MB single-shot on the same slow link. 60s still bounds a half-open
// socket at 1/5th of the old default.
const RECORDING_UPLOAD_TIMEOUT_MS = 60000;
// Client bound on the offline-replay dedupe lookup (getChatsByTempId). Must stay
// under the proxy's ~30 s cut-off: a lookup the proxy kills comes back with no
// CORS headers, which the browser reports as a network error and the response
// interceptor announces as a server outage. An axios abort (ECONNABORTED) is
// deliberately NOT an outage, so a client timeout is what keeps one slow lookup
// from flipping the whole app offline (2026-10-07 premiumhealthcare flap).
const TEMP_ID_LOOKUP_TIMEOUT_MS = 15000;
const PROVIDER_PAYMENT_LINK_ID_MAX_LENGTH = 256;
const hasControlCharacter = (value) =>
  [...value].some((character) => {
    const codePoint = character.codePointAt(0);
    return (
      codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      codePoint === 0x61c ||
      (codePoint >= 0x200b && codePoint <= 0x200f) ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      codePoint === 0x2060 ||
      (codePoint >= 0x2066 && codePoint <= 0x2069) ||
      codePoint === 0xfeff
    );
  });

const validProviderPaymentLinkId = (value) =>
  typeof value === "string" &&
  value.trim().length > 0 &&
  value.length <= PROVIDER_PAYMENT_LINK_ID_MAX_LENGTH &&
  !hasControlCharacter(value);

const normalizeProviderPaymentLinkBaseCommand = (command, errorCode) => {
  const patientUserId = command?.patientUserId ?? null;
  const clinicalProfileId = command?.clinicalProfileId ?? null;
  if (
    (patientUserId !== null && !validProviderPaymentLinkId(patientUserId)) ||
    (clinicalProfileId !== null && !validProviderPaymentLinkId(clinicalProfileId)) ||
    (patientUserId === null && clinicalProfileId === null) ||
    !Array.isArray(command?.items) ||
    command.items.length === 0 ||
    !validProviderPaymentLinkId(command?.shippingMethodId)
  ) {
    throw new TypeError(errorCode);
  }
  const variantIds = new Set();
  const items = command.items.map((item) => {
    if (
      !item ||
      typeof item !== "object" ||
      Array.isArray(item) ||
      !validProviderPaymentLinkId(item.variantId) ||
      !Number.isSafeInteger(item.quantity) ||
      item.quantity <= 0 ||
      variantIds.has(item.variantId)
    ) {
      throw new TypeError(errorCode);
    }
    variantIds.add(item.variantId);
    return {
      variantId: item.variantId,
      quantity: item.quantity,
    };
  });
  return {
    patientUserId,
    clinicalProfileId,
    items,
    shippingMethodId: command.shippingMethodId,
  };
};

export const normalizeProviderPaymentLinkQuoteCommand = (command) =>
  normalizeProviderPaymentLinkBaseCommand(command, "patient_payment_link_quote_request_invalid");

export const normalizeProviderPaymentLinkCreateCommand = (command) => {
  const errorCode = "patient_payment_link_create_request_invalid";
  const canonical = normalizeProviderPaymentLinkBaseCommand(command, errorCode);
  if (!validProviderPaymentLinkId(command?.pricingQuoteId) || !validProviderPaymentLinkId(command?.clientIdempotencyKey)) {
    throw new TypeError(errorCode);
  }
  return {
    ...canonical,
    pricingQuoteId: command.pricingQuoteId,
    clientIdempotencyKey: command.clientIdempotencyKey,
  };
};

const PROVIDER_PAYMENT_LINK_TRANSPORT_MARKER = "__lucasProviderPaymentLinkTransportBoundary";
const providerPaymentLinkErrorDetails = new WeakMap();
const SAFE_MACHINE_CODE_PATTERN = /^[a-z][a-z0-9_]{2,63}$/;
const isProviderPaymentLinkCancellation = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError";
const makeDetachedProviderPaymentLinkCancellation = () => {
  const cancellation = new Error("canceled");
  cancellation.name = "AbortError";
  cancellation.code = "ERR_CANCELED";
  return cancellation;
};
const rethrowDetachedProviderPaymentLinkCancellation = (error) => {
  if (isProviderPaymentLinkCancellation(error)) throw makeDetachedProviderPaymentLinkCancellation();
  throw error;
};

export const getProviderPaymentLinkTransportError = (error) => providerPaymentLinkErrorDetails.get(error) || null;

const sanitizeProviderPaymentLinkQuoteCandidate = (quote) => {
  if (!quote || typeof quote !== "object" || Array.isArray(quote)) return null;
  return {
    pricingQuoteId: quote.pricingQuoteId,
    quoteExpiresAt: quote.quoteExpiresAt,
    pricingVersion: quote.pricingVersion,
    orderContext: quote.orderContext,
    productSubtotalCents: quote.productSubtotalCents,
    shippingMethod:
      quote.shippingMethod && typeof quote.shippingMethod === "object" && !Array.isArray(quote.shippingMethod)
        ? {
            id: quote.shippingMethod.id,
            label: quote.shippingMethod.label,
          }
        : null,
    shippingCents: quote.shippingCents,
    amountDueCents: quote.amountDueCents,
    lines: Array.isArray(quote.lines)
      ? quote.lines.map((line) => ({
          variantId: line?.variantId,
          productName: line?.productName,
          variantName: line?.variantName,
          label: line?.label,
          dosage: line?.dosage,
          quantity: line?.quantity,
          patientPriceCents: line?.patientPriceCents,
          lineTotalCents: line?.lineTotalCents,
        }))
      : null,
  };
};

const sanitizeProviderPaymentLinkErrorOutput = (data) => {
  const output = data?.output;
  if (!output || typeof output !== "object" || Array.isArray(output)) return {};
  const safe = {};
  if (typeof output.code === "string" && SAFE_MACHINE_CODE_PATTERN.test(output.code)) safe.code = output.code;
  if (typeof output.errorCode === "string" && SAFE_MACHINE_CODE_PATTERN.test(output.errorCode)) {
    safe.errorCode = output.errorCode;
  }
  if (Number.isSafeInteger(output.status) && output.status >= 100 && output.status <= 599) safe.status = output.status;
  if (output.code === "pricing_changed" || output.errorCode === "pricing_changed") {
    safe.freshQuote = sanitizeProviderPaymentLinkQuoteCandidate(output.freshQuote);
  }
  return safe;
};

const API_ERROR_SENSITIVE_KEYS = new Set([
  "password",
  "cardnumber",
  "cvv",
  "expmonth",
  "expyear",
  "cardholdername",
  "billingzip",
  "invitetoken",
  "intaketoken",
  "cpinvitetoken",
  "email",
  "phone",
  "mobilephone",
  "firstname",
  "lastname",
  "dob",
  "address",
  "city",
  "state",
  "zip",
  "profile",
  "audio",
]);

const redactApiErrorValue = (value) => {
  if (Array.isArray(value)) return value.map(redactApiErrorValue);
  if (!value || typeof value !== "object") return value;
  const safe = {};
  for (const [key, nested] of Object.entries(value)) {
    safe[key] = API_ERROR_SENSITIVE_KEYS.has(key.toLowerCase()) ? "[REDACTED]" : redactApiErrorValue(nested);
  }
  return safe;
};

export const sanitizeApiErrorRequestBody = (rawBody) => {
  const parsed = typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody;
  return JSON.stringify(redactApiErrorValue(parsed));
};

// A required-documents block comes back as a clean 200 with
// { blocked: true, errorCode: "documents_overdue" } and NO `order` — the same
// idiom the peptide back-order block already uses. The DME callers all read
// `output.order` (and ModalCreateOrder only checks `output.error`), so an
// unhandled block would read as a generic failure or, worse, as a SUCCESS.
//
// Raise a typed error so each call site can skip its generic toast, and publish
// once so the localized toast + the orgInfo refresh that hands over to the route
// guard live in exactly ONE place (RequiredDocsLockHost) instead of at all seven
// create/submit call sites. Non-blocked responses are returned untouched.
const throwIfDocumentsOverdue = (res) => {
  const output = res?.data?.output;
  if (!isDocumentsOverdueOutput(output)) return res;
  publish(DOCUMENTS_OVERDUE_EVENT, { errorMessage: output.errorMessage || null });
  throw makeDocumentsOverdueError(output);
};

// A manufacturer-side product-restriction block comes back the same way:
// { blocked: true, errorCode: "product_restricted", restrictedProducts } and
// no `order`. Raise a typed error so submit call sites can show the per-wound
// list instead of a generic failure. Non-blocked responses pass through.
const throwIfProductRestricted = (res) => {
  const output = res?.data?.output;
  if (!isProductRestrictedOutput(output)) return res;
  throw makeProductRestrictedError(output);
};

export const createApi = () => {
  axiosRetry(axios, { retries: 3 });

  const api = axios.create({
    baseURL: localStorage.getItem("hostName") || process.env.REACT_APP_ENDPOINT,
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "multipart/form-data",
    },
    timeout: 300000,
    withCredentials: true,
  });

  // Promotes config.meta.process onto the wire as an actual header, so the
  // backend's own logs/APM can tell WHICH process a request was for — not just
  // the frontend's after-the-fact error report. Confirmed safe against a real
  // preflight: this API's CORS reflects whatever Access-Control-Request-Headers
  // the browser sends (verified against app.lucashealth.ai on 2026-08-19), so an
  // arbitrary header name doesn't get blocked. Only fires for the handful of
  // callers that set meta.process; everything else is unaffected.
  api.interceptors.request.use((config) => {
    if (config.meta?.process) {
      config.headers = config.headers || {};
      config.headers["X-Api-Process"] = config.meta.process;
    }
    return config;
  });

  api.interceptors.response.use(null, (res) => {
    // Offline mode (spec §1): a network-class failure or gateway error means
    // the SERVER is unreachable, independent of device connectivity. Announce
    // it BEFORE any endpoint-specific special-casing below (review finding
    // M5) — a gateway failure on the provider-payment-link transport used to
    // throw its own sanitized error above this check, so that endpoint's
    // outages never announced serverDown. useAuth owns the serverReachable
    // state and the recovery probe.
    // ECONNABORTED is excluded from THIS path only: it is axios aborting the
    // request client-side when a per-request `timeout` elapses, and several
    // endpoints set deliberately short ones (the 60s recording backstop, the
    // 8s clinical-profile lookup). One slow-but-healthy call must not flip the
    // whole app into outage mode. It stays server-down-class for callers that
    // classify their OWN errors (login/checkAuth), where a timeout on the auth
    // call genuinely does mean the server is unreachable.
    // Fire-and-forget — classification must never mask the original error.
    try {
      if (isServerDownError(res) && res?.code !== "ECONNABORTED" && res?.cause?.code !== "ECONNABORTED") {
        publish("serverDown", { code: res.code, status: res.response?.status });
      }
    } catch (_e) {
      // events util unavailable (tests without window) — never break the error path
    }
    if (res?.config?.[PROVIDER_PAYMENT_LINK_TRANSPORT_MARKER] === true) {
      if (isProviderPaymentLinkCancellation(res)) throw makeDetachedProviderPaymentLinkCancellation();
      const method =
        typeof res.config.method === "string" && /^[a-z]{1,16}$/i.test(res.config.method)
          ? res.config.method.toUpperCase()
          : "POST";
      let pathname = "/web/run-process";
      try {
        const candidate = new URL(res.config.url, "https://transport.invalid").pathname;
        if (candidate === "/web/run-process") pathname = candidate;
      } catch {
        // The marker is set only by the two fixed run-process wrappers.
      }
      const httpStatus = res.response?.status ?? res.status;
      const safeStatus = Number.isSafeInteger(httpStatus) && httpStatus >= 100 && httpStatus <= 599 ? httpStatus : null;
      const safeError = new Error(`API Error\n${method} ${pathname}\nResponse: ${safeStatus ?? "unknown"}`);
      if (safeStatus !== null) safeError.status = safeStatus;
      providerPaymentLinkErrorDetails.set(safeError, {
        status: safeStatus,
        output: sanitizeProviderPaymentLinkErrorOutput(res.response?.data),
      });
      throw safeError;
    }
    let networkError = "";
    if (res.code === "ERR_NETWORK") {
      networkError = " - Server Unreachable - ";
    }
    let error = `API Error ${networkError}\n${res.config.method?.toUpperCase()} ${res.config.url} \nResponse: ${res.status} - ${res.code}`;
    if (res.config.baseURL) {
      error += `\nBase URL: ${res.config.baseURL}`;
    }
    if (res.config.data) {
      let body = "[unavailable]";
      try {
        body = sanitizeApiErrorRequestBody(res.config.data);
      } catch {}

      error += `\\Request body: ${body}`;
    }
    if (res.response && typeof res.response === "object" && res.response.data) {
      error += `\nResponse body: ${typeof res.response.data === "string" ? res.response.data : JSON.stringify(res.response.data)}`;
    }

    // if (res.response.headers) {
    //   error += `\nHeaders: ${res.response.headers}`;
    // }
    // if (res.response.config.headers) {
    //   error += `\nHeaders Config: ${res.response.config.headers}`;
    // }
    // if (res.response.config.withCredentials) {
    //   error += `\nWithCredentials: ${res.response.config.withCredentials}`;
    // }
    // A 403 "No challenge was sent - access forbidden" means the session cookie
    // is missing/expired. Tag it so callers (e.g. the recording resync queue)
    // can prompt re-login instead of treating it as a generic, opaque failure.
    const httpStatus = res.response?.status;
    const responseBody = typeof res.response?.data === "string" ? res.response.data : "";
    let e = new Error(error);
    e.cause = res;
    // WHICH backend call failed. Every request goes to the same /web/run-process
    // URL, so the URL names nothing, and the processId lives in the POST body —
    // which carries clinical audio and must never be parsed for telemetry. Callers
    // that care tag the request instead (config.meta.process), the request
    // interceptor above also sends it as the X-Api-Process header, and it is
    // lifted onto the error here from the config (not the header) so this still
    // works in tests that don't exercise the interceptor.
    // PHI-free by construction: a fixed process name, never patient data.
    if (res?.config?.meta?.process) e.process = res.config.meta.process;
    e.sessionExpired = httpStatus === 403 && /no challenge|access forbidden/i.test(responseBody);
    throw e;
  });

  // A completed response proves the network works, whatever NetInfo's reachability
  // probe believes — see utils/onlineEvidence for why the probe alone cannot recover
  // a session that got stuck offline. Success-only with an untouched pass-through,
  // and publishOnlineEvidence swallows its own errors, so this cannot turn a good
  // response into a failed request.
  //
  // Registered AFTER the error interceptor on purpose: axios keeps handlers in
  // registration order and several api tests reach the rejection handler as
  // `interceptors.response.use.mock.calls[0][1]`. Ordering is irrelevant to
  // behaviour here (nothing is mutated), so the existing index stays valid.
  api.interceptors.response.use((response) => {
    publishOnlineEvidence();
    return response;
  });

  // Security
  const login = (
    user,
    password,
    brain,
    verificationCode,
    agent = "mobile-native",
    trustDevice = true,
    loginToken,
    mfaMode,
    server,
    signOutOtherSessions
  ) => {
    const loginEndpoint = server ? resolveServerEndpoint(server) : getBaseURL();
    if (!loginEndpoint) throw new TypeError("clinical_profile_server_invalid");
    return api.postForm(
      loginEndpoint + `/web/login`,
      JSON.stringify({
        user,
        password,
        brain,
        verificationCode,
        trustDevice,
        agent,
        loginToken,
        mfaMode,
        timeZone: moment.tz.guess(),
        timeZoneOffset: moment().utcOffset(),
        // Only on the user's explicit "sign out my other devices" (AB ignores it with a loginToken anyway).
        ...(signOutOtherSessions ? { signOutOtherSessions: true } : {}),
      })
    );
  };

  const logout = () => {
    return api.postForm(`/web/logout`);
  };

  const getUserProfile = () => {
    return api.get(`/web/user-profile/`);
  };

  const resetPassword = (email, brain) => {
    let callbackUrl = "https://" + window.location.host + "/changePassword";
    // if (sessionStorage.getItem("brand")) {
    //   callbackUrl += "?brand=" + sessionStorage.getItem("brand");
    // }

    return api.post(getBaseURL() + `/web/reset-password`, JSON.stringify({ brain, email, callbackUrl }));
  };

  const changePassword = (email, brain, token, password) => {
    return api.post(getBaseURL() + `/web/change-password`, JSON.stringify({ brain, email, token, password }));
  };

  const createAccount = ({
    email,
    cfToken,
    referralUserId,
    profile,
    agent,
    manufacturer,
    otp,
    organizationName,
    practiceName,
    pricingTier,
    dme,
    peptides,
    requirePsa,
    psaVariantName,
    psaVersion,
    salesRepName,
    salesRepEmail,
    salesRepPhone,
    cpInviteToken,
    cpProfileId,
    cpProviderOrg,
    server,
  }) => {
    const isClinicalProfileInvite = !!(cpInviteToken && cpProfileId && cpProviderOrg);
    const serverEndpoint = resolveServerEndpoint(server);
    if (isClinicalProfileInvite && server && !serverEndpoint) {
      throw new TypeError("clinical_profile_server_invalid");
    }
    const baseURL = serverEndpoint || getBaseURL();
    const callbackUrl = new URL("/login", window.location.origin);
    if (isClinicalProfileInvite) {
      callbackUrl.searchParams.set("cpInviteToken", cpInviteToken);
      callbackUrl.searchParams.set("cpProfileId", cpProfileId);
      callbackUrl.searchParams.set("cpProviderOrg", cpProviderOrg);
      if (server) {
        const normalizedServer = server.toLowerCase();
        callbackUrl.searchParams.set("server", normalizedServer);
        if (normalizedServer !== "app") callbackUrl.searchParams.set("brand", normalizedServer);
      }
    }
    return api.post(
      baseURL + `/web/signup-email`,
      JSON.stringify({
        email,
        cfToken,
        profile,
        agent,
        manufacturer,
        referralUserId,
        // Carry the one-time profile claim and its allowlisted backend through
        // the activation email. This is required when the email opens on a
        // fresh device where the original account-invite localStorage does not
        // exist; Login scrubs and persists the values before authenticating.
        callbackUrl: callbackUrl.toString(),
        otp,
        organizationName,
        practiceName,
        pricingTier,
        dme,
        peptides,
        requirePsa,
        psaVariantName,
        psaVersion,
        salesRepName,
        salesRepEmail,
        salesRepPhone,
        cpInviteToken,
        cpProfileId,
        cpProviderOrg,
      })
    );
  };

  const getOrgStatus = () => {
    return api.get(`/web/organization-status`);
  };

  const checkOrganizationExists = (organizationName) => {
    return api.get(`/web/organization-exists?organizationName=${encodeURIComponent(organizationName)}`, {
      baseURL: process.env.REACT_APP_ENDPOINT,
    });
  };

  const healthCheck = async () => {
    try {
      return await api.get(`/public/status`, { timeout: 10000 });
    } catch (err) {
      if (err?.cause?.response?.status === 404) {
        return api.get(`/`, { timeout: 10000 });
      }

      throw err;
    }
  };

  const getLinkBackoffice = async (mode, brain, userName, agent) => {
    return api.post(
      `/web/login-link`,
      JSON.stringify({
        user: encodeURI(userName),
        mode,
        agent,
        brain,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendLoginLink = async (mode, user, brain) => {
    return api.post(
      `/web/login-push`,
      JSON.stringify({
        user: encodeURI(user),
        mode,
        brain,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const checkLoginRequestStatus = async (requestId, user, brain) => {
    return api.get(`/web/login-push?user=${user}&brain=${brain}&requestId=${requestId}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  // Chats
  const getChatsFiltered = async (brainId, filter) => {
    return api.get(
      `web/organization-chats?brainId=${brainId}&ctx=local:*&status=active,completed,archived&filter=${encodeURIComponent(filter)}`
    );
  };

  // Import Schedule dedup: all encounters whose local:visitTime falls on any of the
  // given "YYYY-MM-DD" days, with the patient loaded for identity matching.
  const getChatsForVisitDays = (brainId, days = []) => {
    const safeDays = days.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
    // No valid days → return empty rather than fetching with an empty filter,
    // which would pull up to 500 arbitrary chats and produce spurious matches.
    if (safeDays.length === 0) return Promise.resolve({ data: { chats: [] } });
    const filter = encodeURIComponent(safeDays.map((d) => `contextVariables['local:visitTime'] ilike '${d}%'`).join(" OR "));
    return api.get(
      `web/organization-chats?brainId=${brainId}&status=active,completed,archived&limit=500&loadUser=true&ctx=local:*&filter=${filter}`
    );
  };

  const getChats = async (brainId, from = "", to = "", locationId = "", filters = {}, limit = 50, abortController) => {
    if (locationId === null) locationId = "";
    const searchFilters = buildChatSearchFilters(filters.name);

    // active,completed,archived,invalid
    const statusFilter = buildStatusFilter(filters);

    // const isInteger = (v) => {
    //   var num = /^-?[0-9]+$/;
    //   return num.test(v);
    // }

    let userIdFilter = "";
    if (filters.userId) {
      userIdFilter += "&userId=" + filters.userId;
    }

    // Only clear locationId when there are actual server-side filters to apply.
    // FIN-format searches have no server filter (contextVariable ilike is unsupported),
    // so keeping locationId lets the server return the normal list for client-side filtering.
    if (searchFilters.hasSearch && (searchFilters.userFilter || searchFilters.encounterFilter)) {
      locationId = "";
    }

    const userFilterParts = [];
    const encounterFilterParts = [];

    if (searchFilters.userFilter) userFilterParts.push(searchFilters.userFilter);
    if (searchFilters.encounterFilter) encounterFilterParts.push(searchFilters.encounterFilter);

    if (filters.insuranceGrid) {
      encounterFilterParts.push("contextVariables['local:insuranceGrid'] = true");
    }

    const userFilterQuery = encodeURIComponent(userFilterParts.join(" and "));
    const encounterFilterQuery = encodeURIComponent(encounterFilterParts.join(" and "));

    const requestUrl = `web/organization-chats?assignedTo=${filters.group || ""}&brainId=${brainId}&locationId=${locationId}&status=${statusFilter}&from=${from}&to=${to}&limit=${limit}&loadUser=true&ctx=local:*&filter=${encounterFilterQuery}${userFilterQuery ? `&userFilter=${userFilterQuery}` : ""}${userIdFilter}`;

    let list = await api.get(requestUrl, { signal: abortController?.signal });

    if (list.status === 200) {
      sendToMobileApp({ type: "onChatListUpdate", chats: list.data.chats });
    }

    return list;
  };

  const getChatsByTempId = async (brainId, tempId) => {
    // The offline temp id is persisted as the context var `local:offlineId`, which
    // the platform stores FLAT (the `local:` prefix is stripped), so the queryable
    // property is `offlineId`. Filtering the nested `contextVariables['local:offlineId']`
    // path never matches — it silently returns 0 rows, which stranded every offline
    // recording in "error" (proven against live data 2026-07-17). See
    // api.getChatsByTempId.test.js.
    const encounterFilterQuery = encodeURIComponent("offlineId = '" + tempId + "'");
    const requestUrl = `web/organization-chats?&brainId=${brainId}&loadUser=true&ctx=local:*&filter=${encounterFilterQuery}`;
    // Bounded on purpose — see TEMP_ID_LOOKUP_TIMEOUT_MS. On a large tenant this
    // filter can hang until the proxy drops it; left unbounded (the 300 s instance
    // default) every hang was announced as serverDown, and the recovery wake
    // re-asked the shell for the same queue, which re-ran the same lookup: the app
    // flapped into offline mode every 60-90 s for as long as the temp id stayed
    // unresolved. A timed-out lookup still defers the diff (no strike, no create),
    // it just stops masquerading as an outage.
    try {
      return await api.get(requestUrl, { timeout: TEMP_ID_LOOKUP_TIMEOUT_MS });
    } catch (e) {
      // Tag the abort so the replay can tell "this one lookup hung" from "the
      // server is gone". The former skips one create and carries on; the latter
      // stops the batch. The response interceptor may hand the abort back
      // wrapped (new Error(msg, { cause })), hence both shapes.
      const code = e?.code || e?.cause?.code;
      if (code === "ECONNABORTED" && e && typeof e === "object") e.tempIdLookupTimedOut = true;
      throw e;
    }
  };

  const getChat = (chatId, requestOptions) => {
    // requestOptions (e.g. { timeout }) is forwarded only when given, so existing
    // callers issue the identical request.
    if (requestOptions) return api.get(`web/organization-chat/${chatId}?ctx=local:*`, requestOptions);
    return api.get(`web/organization-chat/${chatId}?ctx=local:*`);
  };

  // web/organization-chats omits assignedTo from its row projection. Resolve the
  // ChatDatabase assignment relation in one bounded backend process call instead
  // of issuing one filtered organization-chats request per doctor (the N+1 that
  // triggered Sentry LUCASHEALTH-TK).
  //
  // The helper never rejects. `null` means the request failed or returned an
  // unusable body; `{}` is a successful lookup with no assigned encounters. That
  // distinction lets Analytics report partial batch failures without exposing ids.
  const getEncounterAssignees = async (encounterIds, abortController) => {
    if (!encounterIds || encounterIds.length === 0) return {};

    try {
      const response = await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":Get Encounters Assignees",
          parameters: { encounterIds },
        },
        {
          headers: { "Content-Type": "application/json" },
          signal: abortController?.signal,
          // Never amplify a bulk lookup while the backend may already be under
          // load. A failed batch is left unhydrated and counted by the caller.
          "axios-retry": { retries: 0 },
        }
      );
      const assignees = response.data?.output?.assignees;
      const isObject = assignees && typeof assignees === "object" && !Array.isArray(assignees);
      return isObject ? assignees : null;
    } catch (e) {
      // SECURITY: never log/rethrow `e`. The request body carries raw encounter
      // ids, and the axios interceptor can include request data in error text.
      return null;
    }
  };

  const updateChat = (chat) => {
    audit("Update encounter", chat.chatId);
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note - Update",
        chat: chat.chatId,
        parameters: chat,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const callGenericProcess = (process, chatId, params) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + process,
        chat: chatId,
        parameters: params,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updateChatRequest = (chatId, variableName, variableLabel, variableValue) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note Update - Request Approval",
        chat: chatId,
        parameters: {
          variableName,
          variableLabel,
          variableValue,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // `allowUnresolvedLocation` is for offline replay ONLY — see stampCreationLocation.
  // Every interactive path must leave it off so a location-less encounter is
  // refused here rather than created and lost to location-scoped list queries.
  const createChat = async (chat, { allowUnresolvedLocation = false } = {}) => {
    chat = stampCreationLocation(chat, { allowUnresolved: allowUnresolvedLocation });
    let data = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note - Create",
        chat: chat.chatId,
        parameters: chat,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    audit("Create encounter", data.data.output.chat?.id);
    return data;
  };

  // Import Schedule: create an encounter through the duplicate-guarded process.
  // The guard throws DUPLICATE_ENCOUNTER unless allowDuplicate (user override) is set.
  // If the guard process isn't deployed yet, fall back to the plain Note - Create
  // so imports still work — frontend dedup stays active; only the simultaneous-
  // import guard is skipped. This matches the guard's fail-open design and removes
  // a hard deploy-ordering dependency.
  const createChatImport = async (chat, allowDuplicate = false) => {
    chat = stampCreationLocation(chat);
    try {
      let data = await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":Note - Create Import",
          parameters: { ...chat, allowDuplicate },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      audit("Create encounter", data.data.output.chat?.id);
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error || e?.message || "";
      const guardNotDeployed = /Note - Create Import/.test(detail) && /not found/i.test(detail);
      if (guardNotDeployed) {
        return createChat(chat);
      }
      throw e;
    }
  };

  const deleteChat = (chatId) => {
    audit("Delete encounter", chatId);
    return api.putForm(
      `web/organization-chat`,
      JSON.stringify({
        chatId,
        chatLastUpdate: new Date().getTime(),
        status: "invalid",
      })
    );
  };

  const changeStatusChat = (chatId, status) => {
    audit("Change status to " + status, chatId);
    return api.putForm(
      `web/organization-chat`,
      JSON.stringify({
        chatId,
        chatLastUpdate: new Date().getTime(),
        status,
      })
    );
  };

  const unlockChat = (chatId) => {
    audit("Unlock chat", chatId);
    return api.putForm(
      `web/organization-chat`,
      JSON.stringify({
        chatId,
        chatLastUpdate: new Date().getTime(),
        status: "active",
        contextVariables: {
          "local:signedOn": null,
        },
      })
    );
  };

  const generateReportCard = (chat, section, increments) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note Increment Section",
        chat,
        parameters: {
          section,
          increments,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const translateText = (chatId, text, language) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Translate",
        chat: chatId,
        parameters: {
          text,
          language,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendSmsAVS = (chatId, phone, text) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":AVS Send Report SMS",
        chat: chatId,
        parameters: {
          phone,
          text,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendSms = (chatId, phone, text) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Send SMS",
        chat: chatId,
        parameters: {
          phone,
          text,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendSmsWithReminder = (chatId, phone, text, reminderConfig) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Send SMS",
        chat: chatId,
        parameters: {
          phone,
          text,
          reminderConfig: JSON.stringify(reminderConfig),
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getPatientSessions = (patientId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":List Patient Sessions",
        parameters: { patientId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const resendSessionSms = (sessionId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Resend Session SMS",
        parameters: { sessionId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const closeSession = (sessionId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Close Session",
        parameters: { sessionId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updateSessionReminders = (sessionId, reminderConfig) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Update Session Reminders",
        parameters: { sessionId, reminderConfig: JSON.stringify(reminderConfig) },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getSessionQueue = (filters) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":List Session Queue",
        parameters: { filters: JSON.stringify(filters) },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getSessionQueueStats = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Session Queue Stats",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendSessionReminderNow = (sessionId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Send Session Reminder Now",
        parameters: { sessionId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const addToInsuranceGrid = (chatId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note Update - Send Notifications",
        chat: chatId,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendEmailAVS = (chatId, email, text) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":ClearVue Send Report Email",
        chat: chatId,
        parameters: {
          email,
          text,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Patients
  const createPatient = (patient) => {
    // let params = {
    //   "email": "1743172347@p.lucashealth.ai",
    //   "password": "",
    //   "firstName": "test",
    //   "lastName": "x",
    //   "mobilePhone": "393489142471",
    //   "status": "suspended",
    //   "role": "user",
    //   "groups": ["patient"],
    //   "dob": "//",
    //   "custom": { "gender": "m" }
    // };

    return api.postForm(`web/organization-user`, JSON.stringify(patient));
  };

  // A missing id reaches here as the literal string "undefined" — route params
  // stringify, and so does template interpolation — which the backend answers
  // with a SEVERE "User id 'undefined' not found". Reject it before the request
  // so a stale link surfaces as the caller's own error, not a server fault.
  // Rejected rather than thrown so both `await` and `.catch()` call sites see it.
  const getPatient = (patientId) => {
    const id = String(patientId ?? "").trim();
    if (!id || id === "undefined" || id === "null") {
      return Promise.reject(new TypeError("patient_id_missing"));
    }
    return api.get(`/web/user-profile/${id}`);
  };

  const updatePatient = (patient) => {
    // Same endpoint as updateProfile — same rule: customNotes is never echoed.
    return api.put(`/web/user-profile`, JSON.stringify(omitCustomNotesFromProfile(patient)));
  };

  const listPatient = (text, limit = 300) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Search Patients",
        parameters: {
          text,
          limit,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // const listPatient = (filter, limit = 300) => {
  //   let searchFilter = "'patient' in ( out('IsPartOfGroup').name )";
  //   if (filter) {
  //     filter = filter.trim().toLowerCase();
  //     searchFilter += " and ( firstName ilike '" + filter + "%' or lastName ilike '" + filter + "%' )";
  //   }

  //   return api.get(
  //     `web/organization-users?fields=id,firstName,lastName,dob,gender,email,groups,custom.gender,mobilePhone&expression=${encodeURIComponent(searchFilter)}&limit=${limit}`
  //   );
  // };

  const listDoctors = () => {
    return api.get(
      `web/organization-users?fields=id,firstName,lastName,email,groups,address,state,zip,custom.doctorType,role,custom.npiNumber,custom.ptanNumber,custom.noteSettings&filter=groups=doctor`
    );
  };

  // Note: per spec §5, sales-rep pricing tier lives at
  // auth.orgInfo.salesRepTiers[userId] (DoctorOffice-side), not on the user
  // vertex. This query intentionally does NOT include a tier field;
  // SalesRepsView reads the tier map from auth.orgInfo and joins by userId
  // at render time.
  const listSalesReps = () => {
    return api.get(
      `web/organization-users?fields=id,firstName,lastName,email,groups,role,status,custom.invitedBy,custom.organizations,custom.commission,custom.commissionPercent,custom.commissionType,custom.manufacturerPercentage,createdAt&filter=groups=sales-rep;sales-rep-master`
    );
  };

  // Session-scoped sales team for ordinary reps. The backend derives the
  // descendant tree from session:userId and returns only whitelisted fields;
  // unlike organization-users this never sends unrelated reps to the browser.
  const listMySalesTeam = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Sales - List My Team",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Authorized wrapper for reparenting a sales rep — used by the manufacturer
  // Sales Dashboard's Relationship Tree drag-drop. Server-side validates
  // session role + plan + cycle prevention; writes an audit event on success.
  // See backend process: `Sales - Reparent Rep`.
  const salesReparentRep = ({ targetUserId, newInvitedById = null } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Sales - Reparent Rep",
        parameters: { targetUserId, newInvitedById },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Authorized wrapper for moving an account (organization) between two sales
  // reps. Add-then-remove ordering is handled server-side. Writes an audit
  // event on success. See backend process: `Sales - Reassign Account`.
  const salesReassignAccount = ({ organizationId, fromUserId = null, toUserId } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Sales - Reassign Account",
        parameters: { organizationId, fromUserId, toUserId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const listSalesAuditLog = ({
    from = null,
    to = null,
    action = "all",
    actorQuery = "",
    subjectQuery = "",
    subjectId = null,
    cursor = null,
    limit = 50,
  } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Sales - List Audit Log",
        parameters: { from, to, action, actorQuery, subjectQuery, subjectId, cursor, limit },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const exportSalesAuditLogCsv = (filters = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Sales - List Audit Log",
        parameters: {
          from: filters.from ?? null,
          to: filters.to ?? null,
          action: filters.action ?? "all",
          actorQuery: filters.actorQuery ?? "",
          subjectQuery: filters.subjectQuery ?? "",
          subjectId: filters.subjectId ?? null,
          cursor: null,
          limit: 5000,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const listSupervisors = () => {
    return api.get(
      `web/organization-users?fields=id,firstName,lastName,email,groups,role,status,custom.invitedBy,custom.organizations,custom.commission,custom.commissionPercent,custom.commissionType,custom.manufacturerPercentage,createdAt&filter=groups=supervisor`
    );
  };

  const listUsers = () => {
    return api.get(
      `web/organization-users?fields=id,firstName,lastName,dob,gender,email,groups,custom.gender,mobilePhone,role,status&filter=role=supervisor;advanced`
    );
  };

  const findPatient = (firstName, lastName, dob) => {
    return api.get(`/web/organization-users?fields=id&filter=firstName=${firstName},lastName=${lastName},dob=${dob}`);
  };

  const findPatientByEmail = (email) => {
    return api.get(`/web/organization-users?fields=id&filter=email=${email}`);
  };

  // Locations
  const listLocations = () => {
    return api.get(`web/organization-locations`);
  };

  const createLocation = (location) => {
    return api.post(`web/organization-location`, JSON.stringify(location));
  };

  const getLocation = (locationId) => {
    return api.get(`web/organization-location/${locationId}`);
  };

  const updateLocation = (location) => {
    return api.put(`web/organization-location`, JSON.stringify(location));
  };

  const deleteLocation = (locationId) => {
    return api.delete(`web/organization-location/${locationId}`);
  };

  // User
  const updateUser = (user) => {
    return api.put(`/web/organization-user`, JSON.stringify(user));
  };

  const getOrganizationUser = (userId) => {
    return api.get(`/web/organization-user/${userId}`);
  };

  const updateUserCustomFields = async (userId, customUpdates) => {
    const response = await getOrganizationUser(userId);
    const data = response.data || {};
    // GET response shape: { user: {...}, groups: [...], rid: "..." } — groups is a SIBLING of user, not inside it
    const user = data.user || data;
    const groups = data.groups || user.groups || [];
    if (!user || !user.id) throw new Error("User not found: " + userId);
    // Only allow known custom field keys to prevent accidental overwrites
    const allowedCustomKeys = [
      "invitedBy",
      "organizations",
      "commission",
      "commissionPercent",
      "commissionType",
      "manufacturerPercentage",
      "productLines",
    ];
    const sanitizedUpdates = {};
    for (const key of Object.keys(customUpdates)) {
      if (allowedCustomKeys.includes(key)) {
        sanitizedUpdates[key] = customUpdates[key];
      } else {
        console.warn("updateUserCustomFields: ignoring unknown custom field:", key);
      }
    }
    return updateUser({
      userId: user.id || userId,
      status: user.status != null ? user.status : "active",
      role: user.role || "user",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      mobilePhone: user.mobilePhone || "",
      mobilePhoneAuth: user.mobilePhoneAuth || false,
      address: user.address || "",
      city: user.city || "",
      zip: user.zip || "",
      state: user.state || "",
      country: user.country || "",
      language: user.language || "",
      dob: user.dob || "",
      groups,
      custom: { ...user.custom, ...sanitizedUpdates },
    });
  };

  const getUserEmail = () => {
    return api.get(`/web/organization-templates/welcome-user`, JSON.stringify({}));
  };

  const createUser = (user) => {
    return api.post(`/web/organization-user`, JSON.stringify(user));
  };

  // NOTE: a `createSalesRep` helper used to live here, calling a BE process
  // `Sales - Create Rep` that would create the user + write the tier atomically.
  // The BE went with a simpler design (no atomic create process; tier-writes
  // always go through `Sales - Set Rep Tier`). Callers should use
  // `createUser` followed by `setRepTier` instead — see UserEdit.js create
  // path for the two-call pattern + partial-failure UX.

  const setRepTier = (targetUserId, tier) => {
    return api.post(
      `/web/run-process`,
      JSON.stringify({
        processId: localStorage.getItem("organization") + ":Sales - Set Rep Tier",
        parameters: { targetUserId, tier },
      })
    );
  };

  const setRepCustomPricing = (targetUserId, customPrices) => {
    return api.post(
      `/web/run-process`,
      JSON.stringify({
        processId: localStorage.getItem("organization") + ":Sales - Set Rep Custom Pricing",
        parameters: { targetUserId, customPrices },
      })
    );
  };

  const getRepTier = (targetUserId) => {
    return api.post(
      `/web/run-process`,
      JSON.stringify({
        processId: localStorage.getItem("organization") + ":Sales - Get Rep Tier",
        parameters: { targetUserId },
      })
    );
  };

  const getUserPlan = () => {
    return api.post(
      `/web/run-process`,
      JSON.stringify({
        processId: localStorage.getItem("organization") + ":User Plan",
      })
    );
  };

  const getStripePaymentLink = (returnURL, cancelURL) => {
    return api.get("/web/stripe-customer-portal?returnURL=" + encodeURI(returnURL) + "&cancelURL=" + encodeURI(cancelURL));
  };

  const runUserBatch = (action, userIds, callbackUrl = window.location.host) => {
    return api.post("/web/organization-user-batch", JSON.stringify({ action, userIds, callbackUrl }));
  };

  // Organization
  const getOrgInfo = () => {
    return api.post(
      `/web/query-vertices`,
      { userType: "DoctorOffice", expression: "userId is null" },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Fetches the assigned sales rep's name/email/phone via provider-org proxy process.
  // Returns null if the process doesn't exist yet or no rep is assigned.
  // See backend-guides/2026-04-15-sales-rep-on-org-info.md.
  const getSalesRep = async () => {
    try {
      const response = await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":Order - Provider - Get Sales Rep",
          parameters: {},
        },
        { headers: { "Content-Type": "application/json" } }
      );
      return response?.data?.result || null;
    } catch (_e) {
      return null;
    }
  };

  // Manufacturer-side only: fetch a provider's current Required Documents
  // signed state via the cross-org proxy process. Returns zero-state nulls
  // on any error so drawers can render without special-casing.
  // See backend-guides/2026-04-22-require-psa-on-signup.md (Document Status section).
  const getProviderDocumentStatus = async (providerOrgId) => {
    // Schema 2 shape — schema 1 (BAA/PA/NAF/PSA dates only) is still
    // supported via back-compat keys. Expanded keys (signer names, NAF
    // field values, PSA snapshot, etc.) are used by the Required Documents
    // download flow in `ProviderInfoDrawer`.
    const EMPTY = {
      baaSignedAt: null,
      paSignedAt: null,
      nafCompletedAt: null,
      psaSignedAt: null,
      touSignedAt: null,
      touSignerName: null,
      touSignedVersion: null,
      baaSignedRecords: [],
      paSignerName: null,
      nafFieldValues: null,
      psaSignerName: null,
      psaSnapshot: null,
      organizationName: null,
      brand: null,
      lockContracts: [],
      externalSignoffs: null,
      psaPushedAsContract: null,
    };
    if (!providerOrgId) return EMPTY;
    try {
      const response = await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":Order - Manufacturer - Get Provider Document Status",
          parameters: { providerOrgId },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const signedState = response?.data?.output?.signedState;
      return signedState && typeof signedState === "object" ? { ...EMPTY, ...signedState } : EMPTY;
    } catch (_e) {
      return EMPTY;
    }
  };

  const updateOrgInfo = (orgInfo) => {
    // Callers spread `{ ...auth.orgInfo, x }` and post the whole record. The
    // server merges present keys, so a stale `customNotes` array from this
    // session's snapshot would overwrite templates built elsewhere. Strip it:
    // updateOrgCustomNotes is the only writer of that field.
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Update Organization",
        parameters: {
          data: omitCustomNotesFromOrg(orgInfo),
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Sole writer of org-level custom note templates (Ultimate plans). Sends only
  // customNotes; the backend merges present keys, so nothing else is touched.
  const updateOrgCustomNotes = (customNotes) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Update Organization",
        parameters: {
          data: { customNotes },
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Distinct ICD-10 codes the org has used across its encounters, ranked by frequency.
  // Used to pre-fill the org's custom ICD-10 library. Response: data.output.codes = [{code, description, frequency}].
  const getUsedIcd10Codes = (limit) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":ICD-10 Codes - Get Used",
        parameters: {
          limit: limit != null ? String(limit) : "",
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Profile
  const getProfile = () => {
    return api.get(`/web/user-profile/`);
  };

  const updateProfile = (profile) => {
    // The server merges `custom` keys but replaces array values wholesale.
    // Full-profile saves (Settings, "enable this note type", …) run from an
    // in-memory snapshot that may predate templates saved in another tab, the
    // extension panel or the phone — so customNotes is never sent from here.
    // updateProfileCustomNotes is the only writer of that field.
    return api.put(`/web/user-profile`, JSON.stringify(omitCustomNotesFromProfile(profile)));
  };

  // Sole writer of user-level custom note templates and their enablement flags.
  // Pass only what changed; absent keys are left untouched on the server.
  const updateProfileCustomNotes = ({ customNotes, availableNotes } = {}) => {
    const custom = {};
    if (customNotes !== undefined) custom.customNotes = customNotes;
    if (availableNotes !== undefined) custom.availableNotes = availableNotes;
    return api.put(`/web/user-profile`, JSON.stringify({ custom }));
  };

  // NPI verification — ADVISORY, proxied through the backend "NPI - Verify"
  // process (gate-free rebuild; isolated NPI- namespace, won't collide with the
  // reverted "Provider - Verify NPI"). Called silently/fire-and-forget from the
  // onboarding wizard — the provider never sees the result; it only records
  // status for the manufacturer-facing NPI roster, and NEVER blocks ordering.
  //
  // Backend contract ("NPI - Verify"):
  //   Input:  { npi: string (10 digits), firstName, lastName }
  //   On pass: server writes user.custom.{npiVerified, npiVerifiedAt,
  //             npiVerificationSnapshot} and clears npiVerificationFailureReason.
  //             Returns { ok: true, snapshot: { fullName, taxonomy, status } }.
  //   On fail: server returns { ok: false, reason } where reason ∈
  //             { "NOT_FOUND", "INACTIVE", "NAME_MISMATCH", "WRONG_TYPE", "UNKNOWN" }.
  // Verification criteria (server-side, single source of truth):
  //   - NPI exists in NPPES; basic.status === "A"; enumeration_type === "NPI-1";
  //     clinician taxonomy; normalized NPPES legal name matches submitted
  //     firstName + lastName (HARD reject on mismatch).
  const verifyNpi = (npi, firstName, lastName) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":NPI - Verify",
        parameters: {
          npi,
          firstName,
          lastName,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Void-on-change (advisory, silent). Called on the caller's OWN user after a
  // profile save when their NPI number or legal name changed: the backend
  // ":NPI - Invalidate" resets npiVerified + npiManuallyCleared, clears the
  // denormalized manual-clear fields, and appends an immutable npi.changed audit
  // entry (keeps npiVerificationSnapshot). Callers should then re-run verifyNpi
  // with the new values. Fire-and-forget — the provider never sees the result.
  const invalidateNpi = (previousNpiNumber, previousLegalName) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":NPI - Invalidate",
        parameters: {
          previousNpiNumber: previousNpiNumber || "",
          previousLegalName: previousLegalName || "",
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Manufacturer-scoped: list a provider org's signer-capable users + the org
  // NPI, each with verification / manual-clear status. NPI verification is
  // advisory data the manufacturer owns — it never gates ordering, and the
  // provider never sees any of it. The backend verifies + grabs the data
  // (incl. failures) and exposes it here so the manufacturer can review and
  // manually approve. Mirrors getProviderDocumentStatus (cross-org RPC pattern).
  // Response shape is LOCKED by the backend contract:
  //   lucasai-backend/backend-guides/2026-05-27-npi-advisory-backend-contract.md
  // Calls the consolidated action-routed endpoint ":NPI - Manufacturer" with
  // action:"getRoster" → RPC → provider-side ":NPI - Provider". Backend enforces
  // the OrganizationProvider affiliation ACL; no affiliation → zero-state.
  // Expected output shape:
  //   { providers: [{ userId, firstName, lastName, credentials, npiNumber,
  //                   npiVerified, npiVerifiedAt, npiVerificationFailureReason,
  //                   npiManuallyCleared, npiManuallyClearedByName, npiManuallyClearedAt }],
  //     orgNpi: { groupNpi, verified, manuallyCleared, clearedByName, clearedAt } | null,
  //     summary: { total, cleared, unverified } }
  const getProviderNpiRoster = async (providerOrgId) => {
    const EMPTY = { providers: [], orgNpi: null, summary: { total: 0, cleared: 0, unverified: 0 } };
    if (!providerOrgId) return EMPTY;
    try {
      const response = await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":NPI - Manufacturer",
          parameters: { action: "getRoster", providerOrgId },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const out = response?.data?.output;
      return out && typeof out === "object" ? { ...EMPTY, ...out } : EMPTY;
    } catch (_e) {
      return EMPTY;
    }
  };

  // Manufacturer-scoped: manually mark a provider's NPI as cleared for the edge
  // cases the silent NPPES check flagged as wrong (a correct NPI that didn't
  // auto-verify). This is the manufacturer's "manually approve the information"
  // action — the provider is never involved. Calls the consolidated endpoint
  // ":NPI - Manufacturer" with action:"manualClear" → RPC → provider-side
  // ":NPI - Provider", which sets npiManuallyCleared + audit fields (actor from
  // session) and appends an immutable npi.manual_clear entry to
  // DoctorOffice.npiAuditLog[]. Behavior is LOCKED by the backend contract
  // (2026-05-27-npi-advisory-backend-contract.md).
  const manuallyApproveProviderNpi = ({ providerOrgId, userId, note }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":NPI - Manufacturer",
        parameters: { action: "manualClear", providerOrgId, userId, note: note || "" },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Manufacturer-scoped: trigger NPPES verification for ONE specific provider
  // user. NPI verification has no backfill — providers already on file (onboarded
  // before verification, or who never re-saved a changed NPI/name) sit
  // "Unverified" forever even when their NPI is valid + active. Calls
  // ":NPI - Verify - For User" → affiliation-checked RPC → provider-side
  // ":NPI - Verify", which reads the target user's STORED npiNumber + legal name
  // from their own vertex (never trusts this client), runs the NPPES check, and
  // persists npiVerified/npiVerifiedAt/npiVerificationSnapshot/failureReason onto
  // that user. Advisory — never gates ordering; a miss returns
  // { ok:false, reason } (NOT_FOUND/NAME_MISMATCH/...) and is a normal outcome.
  // Used by the AccountsView dashboard-wide sweep of unverified providers.
  const verifyProviderNpiUser = ({ providerOrgId, userId }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":NPI - Verify - For User",
        parameters: { providerOrgId, userId },
      },
      {
        headers: { "Content-Type": "application/json" },
        // Advisory background sweep, fired many-at-once: do NOT let the global
        // axiosRetry replay this POST. A transient failure should fail fast (the
        // sweep swallows it and the module-level attempt-guard prevents a re-fire),
        // not fan out to 4× the NPPES load per provider.
        "axios-retry": { retries: 0 },
      }
    );
  };

  // Jobs
  const ping = () => {
    return api.get(`web/ping`);
  };

  const transcript = (
    audio,
    chatId,
    phase = "",
    language = "en",
    textTranscription,
    recordingId,
    returnContext = true,
    generateAssessment = false,
    transcriptTimestamp = true,
    async = true,
    startedAt,
    forcedPartial = false,
    identity = {}
  ) => {
    const opts = {
      phase,
      language,
      textTranscription,
      recordingId,
      returnContext,
      generateAssessment,
      transcriptTimestamp,
      async,
      startedAt,
      forcedPartial,
      patientUserId: identity?.patientUserId,
      encounterUuid: identity?.encounterUuid,
      overrideIdentity: identity?.overrideIdentity === true,
      recordingStartedAt: identity?.recordingStartedAt,
      offlineId: identity?.offlineId,
      identityGuard: identity?.identityGuard,
    };

    // Large recordings sent as one base64 JSON body are the main cause of
    // ERR_NETWORK on flaky/rural connections (axios then retries the whole
    // multi-MB body). Above the threshold, stream the audio in pieces to the
    // accumulator process; `Transcript - Save` reassembles them on the final call.
    // DEFAULT OFF (2026-07-01, stop-the-bleed revert): the chunked accumulator
    // stored each piece in a `chat:` var the platform silently rejects, so every
    // chunked upload reassembled to "no pieces found" and saved a BLANK note —
    // and because the final Save is async, the throw never reached the client, so
    // it failed silently (the 5-min watchdog was the only symptom). Confirmed live
    // on premiumhealthcare + floridian. Reverted to opt-in until the durable
    // vertex/archive storage fix is re-landed and stage-smoked. Large recordings go
    // single-shot again: a recoverable ERR_NETWORK on a rural link (loud error +
    // durable resync queue re-sends) beats a silent blank note. Opt in per-browser
    // with ff_transcriptChunk="1" (staging, to exercise the storage fix).
    let chunkEnabled = false;
    try {
      chunkEnabled = localStorage.getItem("ff_transcriptChunk") === "1";
    } catch (_) {}
    if (chunkEnabled && typeof audio === "string" && audio.length > TRANSCRIPT_CHUNK_THRESHOLD) {
      return transcriptChunked(audio, chatId, opts);
    }

    return transcriptSingle(audio, chatId, opts);
  };

  const transcriptSingle = (audio, chatId, opts) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Transcript - Save",
        chat: chatId,
        returnContext: opts.returnContext,
        async: opts.async,
        parameters: {
          audio,
          phase: opts.phase,
          language: opts.language,
          generateAssessment: opts.generateAssessment,
          transcriptTimestamp: opts.transcriptTimestamp,
          textTranscription: opts.textTranscription,
          recordingId: opts.recordingId,
          // Real recording-start time (ms epoch, as string to match the other
          // recording params). The backend stamps the transcript block with this
          // instead of the save/sync clock, so recordings that drain together
          // keep distinct, chronological headers and never merge.
          startedAt: opts.startedAt != null ? String(opts.startedAt) : undefined,
          // Cross-device "Recover anyway": the FE is uploading an INCOMPLETE
          // recording's partial transcript under its original recordingId. Sent as
          // the string "true" (trigger vars arrive as strings) ONLY when set, so the
          // backend saves a supersed-able partial and preserves the segment backstop.
          // Omitted otherwise → the backend default (complete save) — every existing
          // caller is byte-for-byte unchanged.
          forcedPartial: opts.forcedPartial ? "true" : undefined,
          // When set (chunked uploads), the backend assembles the audio from the
          // accumulated pieces; harmless/ignored on normal single-shot calls.
          audioTransferId: opts.audioTransferId,
          audioTransferTotal: opts.audioTransferTotal,
          // Recording-to-encounter identity guard (Transcript - Save). Both omitted
          // unless set, so existing callers send byte-for-byte the same body.
          patientUserId: opts.patientUserId || undefined,
          // The encounter's never-recycled UUID the recording was made for; the server
          // refuses the save when the target encounter carries a different one.
          encounterUuid: opts.encounterUuid || undefined,
          overrideIdentity: opts.overrideIdentity ? true : undefined,
          // Guard-only start time (the positional startedAt above is unchanged), the
          // offline id the recording was queued under, and the marker that tells the
          // server this build sends the claim. All omitted unless set.
          recordingStartedAt: opts.recordingStartedAt != null ? String(opts.recordingStartedAt) : undefined,
          offlineId: opts.offlineId || undefined,
          identityGuard: opts.identityGuard || undefined,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        // Names this call in a failure report AND on the wire as X-Api-Process —
        // see the request interceptor.
        meta: { process: "Transcript - Save" },
      }
    );
  };

  // Stream a large base64 audio string to `Transcript - Save Chunk` (a pure
  // accumulator) in ~700KB pieces, then make one normal `Transcript - Save`
  // call carrying `audioTransferId` so the server reassembles + transcribes.
  // Splitting at any boundary is safe: pieces are only concatenated back in
  // order, reproducing the original string exactly.
  const transcriptChunked = async (audio, chatId, opts) => {
    const org = localStorage.getItem("organization");
    const total = Math.ceil(audio.length / TRANSCRIPT_CHUNK_SIZE);
    // Stable per recording: a retry re-encodes the same audio (same length) and
    // overwrites the same server piece files instead of orphaning a new set.
    // When the caller has no recordingId the fallback MUST still be unique —
    // the old literal "x" collided any two same-length recordings into the
    // same server pieces (cross-recording, even cross-user, corruption). A
    // random fallback trades retry-overwrite for uniqueness; orphans from a
    // retried id-less upload are age-swept server-side.
    const fallbackId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    const transferId = `rec-${opts.recordingId || fallbackId}-${audio.length}`;

    for (let index = 0; index < total; index++) {
      const chunk = audio.slice(index * TRANSCRIPT_CHUNK_SIZE, (index + 1) * TRANSCRIPT_CHUNK_SIZE);

      try {
        const res = await api.post(
          "/web/run-process",
          {
            processId: org + ":Transcript - Save Chunk",
            chat: chatId,
            // Sync so each piece is confirmed stored before sending the next.
            async: false,
            parameters: { transferId, index, total, chunk },
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: RECORDING_UPLOAD_TIMEOUT_MS,
          }
        );
        // A 200 alone proves nothing on this platform: the original accumulator
        // returned 200 for every piece while persisting none of them (the 6/30
        // blank-note incident). Only the backend's explicit "chunk-received"
        // echo (set after a verified read-back) counts as stored.
        const status = res?.data?.output?.status ?? res?.data?.status;
        if (status !== "chunk-received") {
          throw new Error(`transcript chunk ${index + 1}/${total} not stored (status: ${status || "missing"})`);
        }
      } catch (e) {
        // If the very first piece fails (e.g. the accumulator is not deployed
        // yet, or storage is rejecting writes), fall back to single-shot so
        // large recordings still go through. A mid-stream failure is rethrown
        // so the durable resync queue retries the whole recording later —
        // never the final assemble call on a partial transfer.
        if (index === 0) {
          return transcriptSingle(audio, chatId, opts);
        }
        throw e;
      }
    }

    // Final call: normal Transcript - Save, no inline audio — the server
    // assembles from the accumulated pieces via audioTransferId.
    // Piece count travels with the final call so the server can reconstruct
    // every deterministic piece fileName without probing (old servers ignore it).
    return transcriptSingle(null, chatId, { ...opts, audioTransferId: transferId, audioTransferTotal: String(total) });
  };

  // Open the server-side manifest the instant a recording starts, so the
  // recording is known to the backend BEFORE any audio uploads. This is what
  // lets another device the user logs into detect a recording stuck on this
  // one. Idempotent on recordingId; fire-and-forget (a failure here must never
  // block recording — the segments themselves still carry recordingId).
  const recordingBegin = (recordingId, chatId, deviceId, deviceLabel, phase = "", startedAt, origin = "web") => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Recording - Begin",
        chat: chatId,
        async: false,
        parameters: {
          recordingId,
          deviceId,
          deviceLabel,
          phase,
          startedAt: startedAt != null ? String(startedAt) : String(Date.now()),
          // "web" | "native". Default "web" matches the BE default so old-FE rows
          // (pre-this-deploy) stay recoverable rather than stranded as null.
          origin,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Upload one independent, self-contained audio segment to the server as an
  // ADDITIVE durability backstop (A2). The backend stores the bytes and bumps the
  // manifest — it does NOT transcribe per-segment. The client still assembles one
  // MP3 from its IndexedDB copy via the durable drain, and that remains the SOLE
  // transcript source; the server copy only exists so an abandoned recording can
  // be recovered cross-device. `isFinal`/`total` advance the manifest counters.
  const recordingSegment = async (recordingId, chatId, index, audioBase64, isFinal, total, language = "en") => {
    const response = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Recording - Store Segment",
        chat: chatId,
        async: false,
        parameters: {
          recordingId,
          index,
          isFinal: !!isFinal,
          total: total || 0,
          language,
          audio: audioBase64,
        },
      },
      {
        headers: { "Content-Type": "application/json" },
        meta: { process: "Recording - Store Segment" },
        // Bound each upload attempt instead of inheriting the 5-minute default.
        // The recorder retries this backstop when the local durable write fails.
        timeout: RECORDING_UPLOAD_TIMEOUT_MS,
      }
    );

    // The process can return HTTP 200 when its archive write failed. Only a
    // matching, explicit storage acknowledgment proves this segment is safe.
    // Avoid numeric coercions such as null/false/"" becoming index zero.
    const segmentIndex = (value) => {
      if (typeof value !== "number" && !(typeof value === "string" && /^\d+$/.test(value))) return null;
      const parsed = Number(value);
      return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
    };
    const output = response?.data?.output;
    const expectedIndex = segmentIndex(index);
    if (
      output?.status !== "stored" ||
      output.recordingId !== recordingId ||
      expectedIndex === null ||
      segmentIndex(output.index) !== expectedIndex ||
      (isFinal && output.isFinal !== true && output.isFinal !== "true")
    ) {
      // Do not include echoed identifiers, audio, or arbitrary backend text.
      throw new Error("Recording segment storage was not confirmed.");
    }
    return response;
  };

  // List the current user's not-done recordings across ALL encounters/devices.
  // Used to detect a recording stuck on another device (e.g. the phone) so we
  // can prompt the user to open it. Returns [] on any failure — this is a
  // best-effort background poll and must never surface an error to the user.
  // The recovery bar and the cross-device hook both poll this on the same ~60s
  // cadence (plus on mount + tab-focus), so they fire near-simultaneously. Dedup with
  // a short-TTL promise cache: two callers within the window share ONE request,
  // halving the pending-list load for every user (the feature is default-on). 10s is
  // well under the 60s poll cadence, so each tick still gets a fresh list.
  let _pendingListCache = { at: 0, promise: null };
  const PENDING_LIST_TTL_MS = 10000;

  // presentIndicesAll / segmentsExpectedAll (BE: listPendingForUser) let the poll run
  // the completeness gate without one Recording - Get Segments call per pending
  // recording. They are OPTIONAL and must degrade to `undefined` = UNKNOWN.
  //
  // The failure direction is the opposite of recordingGetSegments' `parse`, which
  // coerces anything unrecognized to []. That is right there — an empty list reads as
  // "no segments" alongside an INCOMPLETE segmentsExpected, so nothing silently
  // attaches. Here it would be actively harmful: [] is the signal that feeds the
  // empty-read strike budget, so a mangled value would retire a perfectly recoverable
  // recording after ~10 polls. Unknown must mean "ask the server", never "empty".
  //
  // Worth being strict: no array nested inside an object inside an array has crossed
  // /web/run-process before (see the BE guide on stringified arrays arriving as "{}"),
  // so treat any element that is not a finite number as a mangled payload rather than
  // silently dropping it and computing a gate answer from a partial set.
  const parseUnionIndices = (v) => {
    if (v == null) return undefined;
    try {
      const parsed = typeof v === "string" ? JSON.parse(v) : v;
      if (!Array.isArray(parsed)) return undefined;
      const nums = [];
      for (const raw of parsed) {
        const n = typeof raw === "string" ? parseInt(raw, 10) : raw;
        if (!Number.isFinite(n)) return undefined;
        nums.push(n);
      }
      return nums;
    } catch {
      return undefined;
    }
  };
  const parseUnionExpected = (v) => {
    if (v == null) return undefined;
    const n = typeof v === "string" ? parseInt(v, 10) : v;
    return Number.isFinite(n) ? n : undefined;
  };
  const normalizePendingRow = (r) => {
    if (!r || typeof r !== "object") return r;
    const presentIndicesAll = parseUnionIndices(r.presentIndicesAll);
    const segmentsExpectedAll = parseUnionExpected(r.segmentsExpectedAll);
    // Publish the pair only when BOTH are usable — the gate needs both, and a row
    // carrying one without the other would read as permanently incomplete.
    if (presentIndicesAll === undefined || segmentsExpectedAll === undefined) {
      const { presentIndicesAll: _p, segmentsExpectedAll: _e, ...rest } = r;
      return rest;
    }
    return { ...r, presentIndicesAll, segmentsExpectedAll };
  };
  const recordingPendingList = async () => {
    const now = Date.now();
    if (_pendingListCache.promise && now - _pendingListCache.at < PENDING_LIST_TTL_MS) {
      return _pendingListCache.promise;
    }
    const promise = (async () => {
      try {
        const res = await api.post(
          "/web/run-process",
          {
            processId: localStorage.getItem("organization") + ":Recording - Pending List",
            async: false,
            parameters: {},
          },
          { headers: { "Content-Type": "application/json" } }
        );
        const rows = res?.data?.output?.pending || res?.data?.pending || [];
        return Array.isArray(rows) ? rows.map(normalizePendingRow) : [];
      } catch (e) {
        return [];
      }
    })();
    _pendingListCache = { at: now, promise };
    return promise;
  };

  // Owner-gated segment index for ONE recording — the cross-device completeness gate
  // (Path B). The BE JSON.stringifies presentIndices/segments, so parse defensively.
  // Best-effort: any failure returns an empty, INCOMPLETE shape so the caller never
  // mistakes a fetch error for "nothing missing" and silently attaches a partial.
  //
  // DELIBERATELY NOT CACHED, unlike recordingPendingList above. attemptRecover
  // re-calls this as the SILENT-ATTACH safety re-proof — it re-derives completeness
  // from the same data it is about to upload, specifically to catch a server whose
  // two fields disagree. Serving that re-proof from a short-TTL cache would re-prove
  // against the very response being double-checked, quietly defeating the guarantee.
  // Duplicate-call pressure is handled where it originates instead: the poll's
  // in-flight guard and candidate cap in useCrossDeviceRecovery.
  const recordingGetSegments = async (recordingId) => {
    try {
      const res = await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":Recording - Get Segments",
          async: false,
          parameters: { recordingId },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const out = res?.data?.output || res?.data || {};
      // Guarantee an ARRAY for the list fields — the cross-device pull spreads them
      // ([...segments]), so a non-array (e.g. an unexpected BE shape) would throw
      // "is not iterable". Coerce anything non-array to [].
      const parse = (v) => {
        try {
          const parsed = typeof v === "string" ? JSON.parse(v) : v;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      };
      // Coerce segmentsExpected numerically too — the BE may serialize it as a string;
      // a non-number would force isProvablyComplete to always fail (silent-attach quietly
      // degrades to always-nudge). Failure direction is safe, but coerce so silent-attach
      // works when the recording really is complete.
      const expectedRaw = out.segmentsExpected;
      const expectedNum = typeof expectedRaw === "string" ? parseInt(expectedRaw, 10) : expectedRaw;
      return {
        status: out.status,
        presentIndices: parse(out.presentIndices),
        segments: parse(out.segments),
        segmentsExpected: Number.isFinite(expectedNum) ? expectedNum : -1,
      };
    } catch (e) {
      return { status: "error", presentIndices: [], segments: [], segmentsExpected: -1 };
    }
  };

  // Fetch ONE owned segment's audio bytes (base64) for a cross-device rebuild.
  // Deliberately does NOT swallow errors (unlike the list above): Path B must
  // HARD-ABORT the whole recovery if any listed segment fails to fetch — a 404
  // mid-pull means the file was swept between the list snapshot and the pull, and
  // uploading the rest would silently TRUNCATE the transcript. The caller catches
  // and aborts, never skips a 404.
  const recordingGetSegmentBytes = async (recordingId, fileName) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Recording - Get Segment Bytes",
        async: false,
        parameters: { recordingId, fileName },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const out = res?.data?.output || res?.data || {};
    return { status: out.status, file: out.file, mimeType: out.mimeType || "audio/webm", index: out.index };
  };

  const transcriptOnly = (audio, language = "en") => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Transcript",
        parameters: {
          audio,
          language,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const generate = (chatId, elements = [], async = false) => {
    let section = "note";
    if (elements.length > 0) {
      section = "sections: " + elements.join(", ");
    }
    audit("Generate " + section, chatId);

    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note - Hub",
        chat: chatId,
        async,
        parameters: {
          elements,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const generateReassestment = (chatId, idx) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":NewNote - Inpatient - ER - Reassessment",
        chat: chatId,
        async: true,
        parameters: {
          dispoType: "Reassessment",
          idx,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const aiUpdateNote = (chatId, prompt, section, audio, textTranscription, language = "en") => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":AI Update Note",
        chat: chatId,
        parameters: {
          request: prompt,
          language,
          section,
          audio,
          textTranscription,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const aiQuickStart = (audio, availableNoteTypes, textTranscription, locationId = null, language = "en") => {
    // Callers hand us the view filter, which is "" whenever the clinician is
    // looking at "All locations" — resolve it to a real one. Best-effort rather
    // than throwing like createChat: the audio has already been captured and the
    // encounter is created server-side, so refusing here would lose a recording.
    const { user, filterLocation } = getEncounterLocationContext();
    locationId = resolveCreationLocationId({ user, filterLocation, candidate: locationId }) || locationId || null;

    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":AI Quick Start",
        parameters: {
          language,
          audio,
          textTranscription,
          availableNoteTypes,
          locationId,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sign = (chatId, pin, author) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Sign Encounter",
        chat: chatId,
        parameters: {
          pin,
          author,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const computeEncounterData = (chatId, returnContext = true, async) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Compute Patient Profile Updates",
        chat: chatId,
        async,
        returnContext,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updatePatientProfile = (chatId, updates, returnContext = true, async) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Update Patient Profile",
        chat: chatId,
        async,
        returnContext,
        parameters: {
          updates,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createEncounterFromImage = (documentContent, encounterType) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Import Note from File",
        parameters: {
          encounterType,
          documentContent,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const importEncountersFromImage = (documentContent, scheduleDate, noteTypes) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Import Encounters",
        async: true,
        parameters: {
          documentContent,
          ...(scheduleDate ? { scheduleDate } : {}),
          ...(noteTypes ? { noteTypes } : {}),
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const pushNote = (chatId, parameters) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note - Push Note",
        chat: chatId,
        parameters,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const approveRequest = (actionId, status, reason) => {
    return api.post(
      `/web/user-action/${actionId}/${status}`,
      { reason },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const optimizeNote = (hpi, planOfCare, icd10) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Post Note - ICD10 Generate Documentation",
        parameters: {
          hpi,
          planOfCare,
          icd10,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const optimizeNoteUpdateDocs = (originalPlanOfCare, addendumPlanOfCare) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Post Note - ICD10 Update Documentation",
        parameters: {
          originalPlanOfCare,
          addendumPlanOfCare,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createPriorAuth = (chatId, authReason, noteContent, faceSheet, noteTranscript, originEncounter) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":NewNote - Prior Auth - Generic",
        chat: chatId,
        async: true,
        parameters: {
          authReason,
          noteContent,
          faceSheet,
          noteTranscript,
          originEncounters: [originEncounter],
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const regeneateMode = (chatId, mode, section) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Note Part - Regenerate " + section,
        chat: chatId,
        parameters: {
          mode,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createAfterVisitSummary = (chatId, encounterType) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":ClearVue - Generate",
        chat: chatId,
        async: true,
        parameters: {
          encounterType: encounterType,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Documents
  const uploadDocument = (chatId, documentContent, documentId, documentType, async = true) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Upload Document",
        chat: chatId,
        async,
        parameters: {
          documentType, // "images" || "meds" || "records" || "labs"
          documentId,
          documentContent,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Chiro intake AI extraction — runs the backend process "Chiro Intake - Extract"
  // and returns a structured result with fields, warnings, confidence, and meta.
  // `chat` is optional (extraction can run before an encounter is persisted).
  // Server: "{organization}:Chiro Intake - Extract".
  const chiroIntakeExtract = async ({
    chatId,
    text = "",
    images = [],
    transcript = "",
    scope = "full",
    currentNote = {},
    section = "",
    noteType = "chiro-intake",
  } = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Chiro Intake - Extract",
        ...(chatId ? { chat: chatId } : {}),
        parameters: { text, images, transcript, scope, currentNote, section, noteType },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const out = res?.data?.output || {};
    return { fields: out.fields || {}, warnings: out.warnings || {}, confidence: out.confidence || {}, meta: out.meta || {} };
  };

  const chiroNoteSave = async ({ chatId, fields = {}, meta = {} } = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Chiro Note - Save",
        ...(chatId ? { chat: chatId } : {}),
        parameters: { fields, meta },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const out = res?.data?.output || {};
    return { ok: !!out.ok, locked: !!out.locked };
  };

  const chiroNoteGet = async ({ chatId } = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Chiro Note - Get",
        ...(chatId ? { chat: chatId } : {}),
        parameters: {},
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const out = res?.data?.output || {};
    return {
      fields: out.fields || {},
      meta: out.meta || {},
      addendums: out.addendums || [],
      audit: out.audit || [],
      signedOn: out.signedOn || null,
    };
  };

  const chiroNoteAppendEvent = async ({ chatId, kind, text = "", event = "", author = "" } = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Chiro Note - Append Event",
        ...(chatId ? { chat: chatId } : {}),
        parameters: { kind, text, event, author },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const out = res?.data?.output || {};
    return { ok: !!out.ok, entry: out.entry || null, count: out.count || 0, error: out.error || null };
  };

  // Fire-and-forget batch writer for low-priority user-activity events.
  // Thin passthrough to the LogUserActivity process; all buffering/mapping lives
  // in src/utils/activityLog.js. Verified by activityLog.test.js (via injection)
  // plus the stage smoke -- no standalone unit test for this passthrough.
  const logUserActivity = async (events = []) => {
    if (!Array.isArray(events) || events.length === 0) return { ok: false };
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":LogUserActivity",
        parameters: { events },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return res?.data?.output || {};
  };

  const uploadMedia = (documentContent, directory) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Upload Media",
        parameters: {
          documentContent,
          directory,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const uploadDocumentWound = (chatId, title, documentGroupId, imageContent, imageId, imageContentPost = false) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Photo - Upload Wound",
        chat: chatId,
        async: true,
        parameters: {
          imageId,
          imageContent,
          imageContentPost,
          documentGroupId,
          title,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getFile = (fileName) => {
    return api.get("/web/archive-file/" + fileName, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const getMediaFile = (fileName) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Media - Get File",
        parameters: { fileName },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const deleteFile = (fileName) => {
    // Route the archive delete through a process (Media - Delete File) instead of
    // the generic DELETE /web/archive-file/ endpoint. The platform role-gates that
    // raw endpoint per group, so clinical users who can upload (process-mediated)
    // could not delete directly — issue #1287. The process runs the delete with the
    // process's archive permission, mirroring getMediaFile above.
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Media - Delete File",
        parameters: { fileName },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // ICD 10
  const getIcd10 = (text = "") => {
    // Encoded: synonym expansion sends multi-word phrases ("high blood pressure").
    return api.get("/web/shared-database/icd-10-cm?mode=partialMatching&text=" + encodeURIComponent(text), {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const getCptCodes = (text = "") => {
    return api.get("/web/shared-database/cpt-1?mode=partialMatching&text=" + text, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const getCpt2Codes = (text = "") => {
    return api.get("/web/shared-database/hcpcs-2?mode=partialMatching&text=" + text, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  // Qcode
  const getQcodes = (key = "") => {
    return api.get("/web/shared-database/qcodes?mode=partialMatching&key=" + key, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const addQcode = (code) => {
    return api.post("/web/shared-database/qcodes?key=" + code.c, code, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const deleteQcode = (key) => {
    return api.delete("/web/shared-database/qcodes?key=" + key, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const editQcode = (key, code) => {
    return api.put("/web/shared-database/qcodes?key=" + key, code, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  // SmartSheet
  const getSmartSheet = () => {
    return api.get("/web/shared-database/smart-sheet", {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const addSmartSheet = (sheet) => {
    return api.post("/web/shared-database/smart-sheet?key=" + sheet.id, sheet, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const deleteSmartSheet = (key) => {
    return api.delete("/web/shared-database/smart-sheet?key=" + key, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const editSmartSheet = (key, sheet) => {
    return api.put("/web/shared-database/smart-sheet?key=" + key, sheet, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  // Reminder Templates — stored inside the org's DoctorOffice vertex (same as practice info)
  const _getOrgDoc = async () => {
    const res = await api.post(
      "/web/query-vertices",
      { userType: "DoctorOffice", expression: "userId is null" },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.result?.[0] || {};
  };

  const getReminderTemplates = async () => {
    const org = await _getOrgDoc();
    return { status: 200, data: { result: org.reminderTemplates || [] } };
  };

  const addReminderTemplate = async (template) => {
    const org = await _getOrgDoc();
    const templates = org.reminderTemplates || [];
    template.id = "tpl-" + Date.now();
    templates.push(template);
    org.reminderTemplates = templates;
    return api.put(`/web/vertex/${org.id}`, org, { headers: { "Content-Type": "application/json" } });
  };

  const deleteReminderTemplate = async (templateId) => {
    const org = await _getOrgDoc();
    org.reminderTemplates = (org.reminderTemplates || []).filter((t) => t.id !== templateId);
    return api.put(`/web/vertex/${org.id}`, org, { headers: { "Content-Type": "application/json" } });
  };

  const editReminderTemplate = async (templateId, template) => {
    const org = await _getOrgDoc();
    org.reminderTemplates = (org.reminderTemplates || []).map((t) => (t.id === templateId ? { ...template, id: templateId } : t));
    return api.put(`/web/vertex/${org.id}`, org, { headers: { "Content-Type": "application/json" } });
  };

  // Reminder Scheduling
  const createOrderReminder = (orderId, resumeIn, messageContent) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Create Reminder",
        parameters: {
          orderId,
          resumeIn,
          messageContent,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const cancelOrderReminder = (reminderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Cancel Reminder",
        parameters: {
          reminderId,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Analytics
  const getStats = (start, end = 0) => {
    return api.get(
      `/web/organization-stats?begin=${start}&end=${end}&variables=userEHRSyncOn,userCopySectionOn,billableCodesICD10,encounterType,levelOfService,transcriptTotalDuration,generateElapsed,gender,metric*`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // EHR Integration

  const getEhrTemplates = () => {
    return api.post(
      "/web/query-vertices",
      { userType: "IntegrationConfiguration" },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createEhrTemplate = (template) => {
    return api.post("/web/vertex/IntegrationConfiguration", template, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const deleteEhrTemplate = (templateId) => {
    return api.delete("/web/vertex/" + templateId, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const saveEhrTemplate = (templateId, template) => {
    return api.put("/web/vertex/" + templateId, template, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const reportEhrSyncAlert = (templateId, templateName, triggers, currentReport, historyWindow) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":EHR - Sync Alert",
        parameters: {
          templateId: templateId,
          templateName: templateName,
          triggers: triggers,
          currentReport: {
            succeeded: currentReport.succeeded,
            failed: currentReport.failed,
            mismatch: currentReport.mismatch,
            uncertain: currentReport.uncertain,
            resolverStats: currentReport.resolverStats,
          },
          historyWindow: historyWindow,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const ehrProcessContent = (prompt, prevValue, newValue) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":EHR - Process Content",
        parameters: {
          prompt,
          prevValue,
          newValue,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Order Provider
  const getOrders = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - List",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getOrder = (orderId, type = "Provider") => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - " + type + " - Get Order",
        parameters: {
          orderId,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getOrderProducts = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Get Products",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createOrder = async (
    faceSheet,
    clinicalDocumentation,
    woundPicture,
    dateOfService,
    orderUuid,
    useShorelineInsurance,
    photoAttestation,
    officeName,
    officeAddress,
    officeCity,
    officeState,
    officeZip,
    woundType,
    dmeProvider
  ) => {
    return throwIfDocumentsOverdue(
      await api.post(
        "/web/run-process",
        {
          processId: localStorage.getItem("organization") + ":Order - Provider - Create",
          parameters: {
            faceSheet,
            clinicalDocumentation,
            woundPicture,
            dateOfService,
            orderUuid,
            useShorelineInsurance,
            photoAttestation,
            officeName,
            officeAddress,
            officeCity,
            officeState,
            officeZip,
            woundType,
            // Org's default DME (or the manufacturer sentinel) so the order is born with one.
            dmeProvider,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
    );
  };

  // `clinicalDocumentationUploading` / `facesheetUploading` are transient
  // client-side spinner flags — the backend neither reads nor writes them, but
  // Order - * - Update persists whatever the payload contains. Callers spread
  // the whole order, so an update issued while an upload was in flight (e.g.
  // replacing a document, marking comments read) stamped `true` onto the stored
  // order and the attachment row then spun on every open, forever. Strip them
  // here rather than at ~20 call sites; sending `false` also heals orders
  // already poisoned by earlier writes.
  const updateOrder = (order, type = "Provider") => {
    const payload = stripTransientUploadFlags(order);
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - " + type + " - Update",
        parameters: { order: payload, organizationId: order.organizationId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const verifyInsurance = (orderUuid, dateOfService, idempotencyKey) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Verify Insurance BG",
        parameters: { orderUuid, dateOfService, idempotencyKey },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const runInsuranceDiscovery = (orderUuid, idempotencyKey) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Insurance Discovery BG",
        parameters: { orderUuid, idempotencyKey },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // const requestOrderCode = () => {
  //   return api.post(
  //     "/web/run-process",
  //     {
  //       processId: localStorage.getItem("organization") + ":Request Order Code",
  //     },
  //     {
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //     }
  //   );
  // };

  const submitOrder = async (orderId, status, pin, consentData) => {
    return throwIfProductRestricted(
      throwIfDocumentsOverdue(
        await api.post(
          "/web/run-process",
          {
            processId: localStorage.getItem("organization") + ":Order - Provider - Submit Order",
            parameters: {
              orderId,
              status,
              pin,
              consentData,
            },
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      )
    );
  };

  const unlockSignedOrder = (orderId, reason) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Unlock Signed Order",
        parameters: {
          orderId,
          reason,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const addComment = (orderId, comment, type = "Provider", attachments) => {
    const parameters = { orderId, comment };
    if (attachments && attachments.length > 0) parameters.attachments = attachments;
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - " + type + " - Add Comment With Attachments",
        parameters,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Reads a per-comment attachment back from the manufacturer's archive,
  // base64-encoded inline. `type` is "Provider" or "Manufacturer" and routes
  // to the matching BE process (each enforces its own role gate; the wrapper
  // forwards identity for cross-org reads). Returns { base64, mimeType,
  // originalName, fileName }. The BE may throw 425 (still scanning), 410
  // (quarantined as infected), or 404 (not found) — callers should normalize
  // those into user-facing toasts rather than the raw error object. Inline
  // bytes (not a signed URL) because `platform.bucket.signGet` is not wired
  // on the assistant-v2 brain.
  const getOrderCommentAttachmentSignedUrl = async (orderId, fileName, type = "Provider") => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - " + type + " - Get Comment Attachment Signed URL",
        parameters: { orderId, fileName },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res?.data?.output?.result;
  };

  const uploadOrderDocument = (orderId, base64, type, async = false) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Upload Document",
        async,
        parameters: {
          orderId,
          base64,
          type,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const uploadOrderDocumentManufacturer = (orderId, base64, type) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Upload Document",
        parameters: {
          orderId,
          base64,
          type,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getDocumentProvider = (fileName) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Get File",
        parameters: {
          fileName,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const archiveOrder = (orderId, isArchived, type = "Provider") => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - " + type + " - Cancel Order",
        parameters: {
          orderId,
          isArchived,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const regenerateMedicalNecessity = (orderId, type = "Provider") => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - " + type + " - Regenerate Medical Necessity",
        parameters: {
          orderId,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Order Manufacturer
  const getOrdersManufacturer = (organizationId, fromDate = 0) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - List",
        parameters: { organizationId, fromDate },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const changeStatusOrder = (orderId, status, parameters = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Update Status",
        parameters: {
          orderId,
          status,
          comment: parameters.comment,
          trackingCode: parameters.trackingCode,
          shipmentDate: parameters.shipmentDate,
          deliveryDate: parameters.deliveryDate,
          carrier: parameters.carrier,
          // Manufacturer's per-order DME pick. Backend persists this on the order during
          // approve/reject when the provider-side DME is "Use Manufacturer's DME".
          manufacturerDmeProvider: parameters.manufacturerDmeProvider,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const addInternalNote = (orderId, note) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Add Note",
        parameters: {
          orderId,
          note,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Supervisor correction of shipment / delivery facts on an order that already has
  // them. Deliberately NOT api.updateOrder: the generic "Order - Manufacturer - Update"
  // process strips shipmentDate, carrier, trackingCode and deliveryDate before writing,
  // so edits sent that way are silently dropped by the server while the client shows a
  // success toast. This hits the narrow, role-gated "Update Shipment Info" process,
  // which validates the edit and appends its own history entry.
  // mode is "shipment" (shipmentDate + carrier + trackingCode) or "delivery" (deliveryDate).
  const updateShipmentInfo = (orderId, organizationId, mode, fields) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Update Shipment Info",
        parameters: {
          orderId,
          organizationId,
          mode,
          ...fields,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Supervisor ship-to override on a shipped / delivered order. Deliberately NOT
  // api.updateOrder: the generic "Order - Manufacturer - Update" process strips
  // shippingAddress / shippingCity / shippingState / shippingZip so a debounced save
  // cannot plant or wipe a ship-to without an audit entry. This narrow, role-gated
  // process validates the address and appends its own history. It never writes the
  // patient address of record.
  // action is "set" (all four shipping* fields required) or "clear" (null all four).
  const updateShipTo = (orderId, organizationId, action, fields) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Update Ship To",
        parameters: {
          orderId,
          organizationId,
          action,
          ...fields,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const setOrderUrgent = (orderId, isUrgent) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Set Urgent",
        parameters: {
          orderId,
          isUrgent,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getDocumentManufacturer = (fileName) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Get File",
        parameters: {
          fileName,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const setOrderDelivered = (parameters) => {
    const org = parameters.manufacturerOrganizationId || parameters.organizationId;
    return api.post(
      "/api/v1/process-execute",
      { parameters },
      {
        headers: {
          "x-api-process": org + ":Order - Manufacturer - Track Open Package",
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Sales Rep Dashboard
  const getOrdersStats = (from = 0) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Stats",
        parameters: { from },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Manufacturer/supervisor-scoped peptide stats. Returns per-org rollups that
  // include the owning rep's delivered-only commission (`repEarningsCents`) plus
  // peptide order counts — the secure source for sales-dashboard peptide
  // commission (item-level margin detail stays server-side). Windowed by `from`
  // (createdOn epoch ms) so it tracks the dashboard's date-range selector.
  const getPeptideStats = (from = 0) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Stats",
        parameters: { from },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // DME Credentialing
  const getCredentialingRequests = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":DME - Get Credentialing Requests",
        parameters: {},
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updateCredentialingRequest = (requestId, data) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":DME - Update Credentialing Request",
        parameters: { requestId, ...data },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const sendCredentialingMessage = (requestId, message, options = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":DME - Send Credentialing Message",
        parameters: { requestId, message, ...options },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const uploadCredentialingDocument = (requestId, base64, documentType, itemId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":DME - Upload Credentialing Document",
        parameters: { requestId, base64, documentType, itemId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createCredentialingReminders = (requestId, reminders, message, options = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":DME - Create Credentialing Reminders",
        parameters: { requestId, reminders, message, ...options },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Uber Direct — all backed by ArcadeBrain processes via /web/run-process.
  // Outputs land under res.data.output.* (per this platform's run-process envelope).
  const runUberProcess = (processName, parameters = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + processName,
        parameters,
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
  };

  const uberDirectConnect = (credentials) => {
    return runUberProcess("Uber Direct - Connect", credentials || {});
  };

  // No dedicated "test connection" process — Connect with dryRun validates the
  // OAuth handshake without persisting credentials.
  const uberDirectTestConnection = (credentials) => {
    return runUberProcess("Uber Direct - Connect", { ...(credentials || {}), dryRun: true });
  };

  const uberDirectDisconnect = () => {
    return runUberProcess("Uber Direct - Disconnect");
  };

  const uberDirectCreateQuote = (payload) => {
    return runUberProcess("Uber Direct - Create Quote", payload || {});
  };

  const uberDirectCreateDelivery = (payload) => {
    return runUberProcess("Uber Direct - Create Delivery", payload || {});
  };

  const uberDirectGetDelivery = (deliveryId) => {
    return runUberProcess("Uber Direct - Get Delivery", { deliveryId });
  };

  const uberDirectGetDeliveries = (orderId) => {
    return runUberProcess("Uber Direct - Get Deliveries", { orderId });
  };

  const uberDirectCancelDelivery = (deliveryId) => {
    return runUberProcess("Uber Direct - Cancel Delivery", { deliveryId });
  };

  // ── Peptide Catalog & Pricing ──
  // Uses the existing getOrgInfo / updateOrgInfo pattern.
  // Data lives as JSON fields on the manufacturer's DoctorOffice vertex:
  //   peptideCatalog, peptideTierPricing, peptideProviderTiers
  // updateOrgInfo is a full replacement, so savePeptideData does
  // a read-modify-write: fetch current org → merge peptide fields → save.

  const getPeptideData = async () => {
    const res = await getOrgInfo();
    const org = res?.data?.result?.[0] || {};
    return {
      catalog: org.peptideCatalog || [],
      tierPricing: org.peptideTierPricing || {},
      providerTiers: org.peptideProviderTiers || {},
      // Per-provider custom pricing overrides, keyed by providerOrgId → { variantId → cents }.
      // Source: manufacturer→provider OrganizationProvider edge (same vertex as pricingTier).
      // Backend projects this from the edge sweep that already populates providerTiers.
      providerCustomPricing: org.peptideProviderCustomPricing || {},
      // Per-provider default for the cart's recurring-order toggle.
      providerRecurringDefaults: org.peptideProviderRecurringDefaults || {},
      // Per-provider active-form surcharge opt-out map ({ providerOrgId: true }).
      // Exceptions-only; absent key = surcharge ON. Read by the admin settings page.
      providerSurchargeDisabled: org.peptideProviderSurchargeDisabled || {},
      // Per-variant manufacturer cost, written by the Save Cost process to
      // DoctorOffice.peptideCostData[variantId]. Without this here the
      // catalog editor's Cost field hydrates as blank on every reopen even
      // though the backend has persisted the value.
      costData: org.peptideCostData || {},
      // Raw shipping-method config ({ enforceSelection, methods } or null).
      // Normalized (defaults applied) by utils/peptideShippingMethods.js in
      // usePeptidePricing — mirrors the BE resolution so the manufacturer
      // checkout shows the same options the server will price.
      shippingSettings: org.peptideShippingSettings || null,
      // Org-level master legal disclaimer ({ reconstitution, restrictedSale,
      // researchUseWarning, manufacturingQuality } or null). null = the org has
      // not customized it, so the frozen default constant applies. When set, it
      // is ALSO stamped per-product as product.legalDisclaimer (the cross-org
      // carrier); this org field is the editor's source of truth for prefilling.
      legalDisclaimer: org.peptideLegalDisclaimer || null,
    };
  };

  const savePeptideData = async (data) => {
    // data can be any subset of { peptideCatalog, peptideTierPricing, peptideProviderTiers }
    // Fetch current org, merge peptide fields, save full object
    const res = await getOrgInfo();
    const currentOrg = res?.data?.result?.[0] || {};
    return updateOrgInfo({ ...currentOrg, ...data });
  };

  // Save per-variant cost via the manufacturer-only Save Cost process.
  // Backend writes to DoctorOffice.peptideCostData[variantId]. Returns the
  // process output. Called from CatalogProductEditor's flushPendingCost on
  // Save Changes, and from PricingTierManager's CostInput onBlur.
  const saveCostData = async (variantId, costCents) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide - Manufacturer - Save Cost",
        parameters: { variantId, costCents },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output;
  };

  // Assign a pricingTier to a single provider on the manufacturer→provider
  // OrganizationProvider vertex. Replaces the legacy peptideProviderTiers map
  // that used to live on the manufacturer's DoctorOffice.
  const assignProviderPricingTier = (providerOrgId, pricingTier) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Assign Provider Pricing Tier",
        parameters: { providerOrgId, pricingTier },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Platform-admin only (server re-checks). Toggles the active-form surcharge
  // for one downstream provider on DoctorOffice.peptideProviderSurchargeDisabled.
  const setProviderSurcharge = (providerOrgId, enabled) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Set Provider Surcharge",
        parameters: { providerOrgId, surchargeEnabled: enabled },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Save per-provider custom price overrides (and optional recurring default)
  // on the manufacturer's DoctorOffice — sibling fields to peptideTierPricing
  // / peptideProviderTiers. Pass an empty customPrices object to clear all
  // overrides for that provider.
  //
  // Routes through the dedicated "Save Provider Custom Pricing" process
  // (S07): manufacturer-plan gated, server-side read-merge-write, input
  // sanitization for cents values. Replaces the previous read-getPeptideData
  // / mutate / savePeptideData / updateOrgInfo path which trusted any
  // manufacturer caller and shipped the full org payload over the wire.
  //
  // Race posture matches the previous implementation: last-write-wins
  // between concurrent edits, now resolved server-side rather than over the
  // network. Recurring default is shipped in the same call when present;
  // an empty object means "don't touch recurring."
  const saveProviderCustomPricing = async (providerOrgId, customPrices, recurringDefault) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Save Provider Custom Pricing",
        parameters: {
          providerOrgId,
          customPrices: customPrices || {},
          recurringDefault: recurringDefault || {},
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output;
    if (output && output.success === true) {
      return output.customPrices || {};
    }
    return customPrices || {};
  };

  // S07(BE): provider-scoped write of the caller's OWN retail map to
  // DoctorOffice.peptidePatientPricing. Server must verify the caller is the
  // provider and re-enforce the floor (never below the provider's wholesale).
  const saveProviderRetailPricing = async (patientPricing) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Save Patient Pricing",
        parameters: { patientPricing: patientPricing || {} },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output;
    if (output && output.success === true) {
      return output.patientPricing || {};
    }
    return patientPricing || {};
  };

  // Returns the peptideProviderCustomPricing map scoped to provider orgs
  // the calling sales rep directly owns. Supervisors / admins receive the
  // full map. Powers the sales-rep in-drawer per-variant editor's baseline
  // values so saved overrides survive drawer reopen. Empty map ({}) when the
  // caller owns no peptide-enabled providers OR none of them have overrides.
  // Backend: lucasai-backend Order - Manufacturer - Get Owned Provider
  // Custom Pricing (PR #101).
  const getOwnedProviderCustomPricing = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Get Owned Provider Custom Pricing",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output;
    return output?.providerCustomPricing || {};
  };

  // Re-enables editing on a provider's signed New Account Form. Only callable
  // by a sales rep or manufacturer upstream of the target provider — backend
  // must validate the caller's role + relationship and write the audit record.
  // See docs/backend-unlock-naf-editing.md for the full spec.
  const unlockProviderNafEditing = async (providerOrgId, reason) => {
    const response = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Unlock Provider NAF Editing",
        parameters: { providerOrgId, reason },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    // The process Finalize returns {ok, editUnlock, error?} via the
    // process engine. HTTP 200 + ok:false is how the backend signals a
    // structured failure (bad affiliation, empty reason, provider NAF
    // not completed, etc.) — without this check, callers treat silent
    // rejections as success and show a misleading green toast.
    const output = response?.data?.output || {};
    if (output.ok !== true) {
      const err = new Error(output.error || "unknown_error");
      err.code = output.error || "unknown_error";
      throw err;
    }
    return output.editUnlock || null;
  };

  // Manufacturer supervisor marks one of a provider's lock-relevant documents
  // as signed outside the system (enabled) or clears that mark. Same contract
  // as unlockProviderNafEditing: HTTP 200 + ok:false is a structured refusal
  // and must throw, never read as success. Full error-code contract
  // (backend-guides/2026-10-19-required-docs-external-signoff.md):
  // not_supervisor | not_affiliated | not_configured | invalid_doc |
  // invalid_enabled | contract_not_found | not_contract_author |
  // doctor_office_not_found | unknown_error.
  const setProviderDocExternalSignoff = async (providerOrgId, { docKind, contractId, enabled, note } = {}) => {
    const response = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Set Provider Doc External Signoff",
        parameters: { providerOrgId, docKind, contractId: contractId || null, enabled, note: note || null },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = response?.data?.output || {};
    if (output.ok !== true) {
      const err = new Error(output.error || "unknown_error");
      err.code = output.error || "unknown_error";
      throw err;
    }
    return output.signoffs || null;
  };

  // Manufacturer supervisor/admin only (server re-checks). Reads one provider's
  // DME product deny-list: { disabledProducts, updatedOn, updatedByLabel, lastPush }.
  const getProviderProductRestrictions = (providerOrgId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Get Provider Product Restrictions",
        parameters: { providerOrgId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Saves the deny-list (exact product keys) and pushes it to the provider org.
  // Response output: { ok, synced, syncError } — synced:false means the save
  // landed but the provider copy did not; saving again retries the push.
  const setProviderProductRestrictions = (providerOrgId, disabledProducts) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Manufacturer - Set Provider Product Restrictions",
        parameters: {
          providerOrgId,
          disabledProducts: JSON.stringify(Array.isArray(disabledProducts) ? disabledProducts : []),
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  /**
   * Upload a peptide product image to the backend archive.
   * Accepts a base64 string (with or without data URI prefix).
   * Returns the archive fileName (use with getFile to retrieve).
   */
  const uploadPeptideImage = async (base64Content) => {
    const res = await uploadMedia(base64Content, "catalog");
    return res.data?.output?.fileName;
  };

  /**
   * Fetch a file from the manufacturer's archive via cross-org RPC.
   * Used by providers to load peptide product images uploaded by the manufacturer.
   */
  const getPeptideFile = async (fileName, { isManufacturer } = {}) => {
    const processName = isManufacturer ? "Peptide - Manufacturer - Get File" : "Peptide - Provider - Get File";
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + processName,
        parameters: { fileName },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const uploadPeptidePaymentReceipt = async (orderId, base64Content, paymentMethod = "zelle", { isManufacturer } = {}) => {
    // Provider goes through the cross-org RPC process; manufacturer writes
    // straight into its own archive via the manufacturer-side process.
    const processName = isManufacturer
      ? "Peptide Order - Manufacturer - Upload Payment Receipt"
      : "Peptide Order - Provider - Upload Payment Receipt";
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + processName,
        parameters: { orderId, base64: base64Content, paymentMethod },
      },
      {
        // Override the axios instance default of multipart/form-data; the
        // process-execute endpoint expects a JSON body.
        headers: { "Content-Type": "application/json" },
      }
    );
  };

  // Peptide Orders
  const getPeptideOrders = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - List",
        parameters: {},
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Returns the calling provider's manufacturer's local-pickup config so the
  // provider checkout can offer "collect from manufacturer". Server resolves the
  // provider→manufacturer edge and reads the manufacturer org's pickup fields.
  const getPeptideManufacturerPickupInfo = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Manufacturer Pickup Info",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const createPeptideOrder = (orderData) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Create",
        parameters: {
          order: orderData,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Charges a peptide order's card via Maverick. Never log the request body — it contains PAN/CVV.
  const chargePeptideOrderCard = (orderId, { cardNumber, expMonth, expYear, cvv, cardholderName, billingZip } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Charge Card",
        parameters: { orderId, cardNumber, expMonth, expYear, cvv, cardholderName, billingZip },
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
  };

  // Manufacturer-side Maverick charge — same shape and contract as the
  // provider process. Used when a manufacturer opts into "Collect payment
  // now" at checkout instead of leaving the order as a draft for the
  // downstream provider to pay. Mirror of `Peptide Order - Provider -
  // Charge Card`; backend handler is identical except the auth gate keys
  // on the manufacturer plan + that the order's source === "manufacturer".
  const chargePeptideOrderCardManufacturer = (
    orderId,
    { cardNumber, expMonth, expYear, cvv, cardholderName, billingZip } = {}
  ) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Manufacturer - Charge Card",
        parameters: { orderId, cardNumber, expMonth, expYear, cvv, cardholderName, billingZip },
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
  };

  // Charges a PATIENT-placed peptide order's card via Maverick (patient-gated
  // proxy). Same interface and contract as chargePeptideOrderCard — the backend
  // process validates that the caller is the patient who placed the order.
  const chargePatientPeptideOrderCard = (orderId, { cardNumber, expMonth, expYear, cvv, cardholderName, billingZip } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Charge Card",
        parameters: { orderId, cardNumber, expMonth, expYear, cvv, cardholderName, billingZip },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const runPatientPricingProcess = (processName, parameters, { signal } = {}, providerPaymentLinkTransport = false) =>
    api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + processName,
        parameters,
      },
      {
        headers: { "Content-Type": "application/json" },
        ...(signal ? { signal } : {}),
        ...(providerPaymentLinkTransport ? { [PROVIDER_PAYMENT_LINK_TRANSPORT_MARKER]: true } : {}),
      }
    );

  const getPatientPeptidePricingDetail = ({ patientUserId = null, clinicalProfileId = null } = {}, options) =>
    runPatientPricingProcess("Peptide - Provider - Get Patient Pricing Detail", { patientUserId, clinicalProfileId }, options);

  const getPatientPeptidePricingHistory = (
    { patientUserId = null, clinicalProfileId = null, cursor = null, limit = 20 } = {},
    options
  ) => {
    const validPatientUserId = patientUserId === null || (typeof patientUserId === "string" && patientUserId.length > 0);
    const validClinicalProfileId =
      clinicalProfileId === null || (typeof clinicalProfileId === "string" && clinicalProfileId.length > 0);
    if (!validPatientUserId || !validClinicalProfileId || (patientUserId === null && clinicalProfileId === null)) {
      throw new TypeError("patient_pricing_history_identity_invalid");
    }
    if (!Number.isInteger(limit) || limit < 1 || limit > 50) {
      throw new TypeError("patient_pricing_history_limit_invalid");
    }
    if (cursor !== null && (typeof cursor !== "string" || cursor.length === 0 || cursor.length > 2048)) {
      throw new TypeError("patient_pricing_history_cursor_invalid");
    }
    return runPatientPricingProcess(
      "Peptide - Provider - Get Patient Pricing History",
      { patientUserId, clinicalProfileId, cursor, limit },
      options
    );
  };

  const savePatientPeptidePricingDetail = (
    { patientUserId = null, clinicalProfileId = null, expectedVersion, customPrices, clientIdempotencyKey } = {},
    options
  ) =>
    runPatientPricingProcess(
      "Peptide - Provider - Save Patient Pricing Detail",
      { patientUserId, clinicalProfileId, expectedVersion, customPrices, clientIdempotencyKey },
      options
    );

  const runProviderPaymentLinkProcess = (processName, command, options) =>
    runPatientPricingProcess(processName, command, options, true).catch(rethrowDetachedProviderPaymentLinkCancellation);

  const quotePatientPeptidePaymentLink = (command, options) =>
    runProviderPaymentLinkProcess(
      "Peptide Order - Provider - Quote Patient Payment Link",
      normalizeProviderPaymentLinkQuoteCommand(command),
      options
    );

  const createPatientPeptidePaymentLink = (command, options) =>
    runProviderPaymentLinkProcess(
      "Peptide Order - Provider - Create Patient Payment Link",
      normalizeProviderPaymentLinkCreateCommand(command),
      options
    );

  const revokePatientPeptidePaymentLink = (orderId, options) =>
    runPatientPricingProcess("Peptide Order - Provider - Revoke Patient Payment Link", { orderId }, options);

  const getPatientPeptidePaymentLink = (token, options) =>
    runPatientPricingProcess("Peptide Order - Patient Payment Link - Get", { token }, options);

  const chargePatientPeptidePaymentLink = (
    token,
    { cardNumber, expMonth, expYear, cvv, cardholderName, billingZip } = {},
    idempotencyKey,
    options
  ) =>
    runPatientPricingProcess(
      "Peptide Order - Patient Payment Link - Charge",
      {
        token,
        cardNumber,
        expMonth,
        expYear,
        cvv,
        cardholderName,
        billingZip,
        clientIdempotencyKey: idempotencyKey,
      },
      options
    );

  const getPeptideOrder = (orderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Get",
        parameters: {
          orderId,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updatePeptideOrder = (orderId, orderData) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Update",
        parameters: { orderId, order: orderData },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updatePeptideOrderStatus = (orderId, status, { cancelReason, carrier, trackingNumber } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Update Status",
        parameters: { orderId, status, cancelReason, carrier, trackingNumber },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Peptide Orders - Manufacturer
  const getPeptideOrdersManufacturer = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - List",
        parameters: {},
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const getPeptideOrderManufacturer = (orderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Get",
        parameters: { orderId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updatePeptideOrderManufacturer = (orderId, orderData) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Update",
        parameters: { orderId, order: orderData },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Internal staff note on a peptide order — manufacturer session only.
  // Append when noteId is null; edit that note in place when set. The server
  // rejects empty text and derives attribution from the session. Notes are
  // stored on their own vertex and never reach provider/patient responses.
  const savePeptideInternalNote = (orderId, note, noteId = null) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Save Internal Note",
        parameters: { orderId, note, noteId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const updatePeptideOrderStatusManufacturer = (orderId, status, { cancelReason, carrier, trackingNumber } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Update Status",
        parameters: { orderId, status, cancelReason, carrier, trackingNumber },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createPeptideOrderManufacturer = (orderData) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Manufacturer - Create",
        parameters: {
          order: orderData,
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Non-manufacturer add-comment. `role` selects the patient or provider
  // wrapper process — those wrappers internally forward identity into the
  // direct manufacturer process via a cross-org RPC so the comment is
  // creator-stamped correctly. Missing/invalid role throws synchronously
  // (no silent fall-through — a wrong wrapper would tag the comment with
  // the wrong creator role and break the bubble alignment).
  const addPeptideOrderComment = (orderId, comment, attachments, role) => {
    if (role !== "patient" && role !== "provider") {
      throw new Error(`addPeptideOrderComment: invalid role "${role}" (must be "patient" or "provider")`);
    }
    const parameters = { orderId, comment };
    if (attachments && attachments.length > 0) parameters.attachments = attachments;
    const processSuffix =
      role === "patient"
        ? "Peptide Order - Patient - Add Comment With Attachments"
        : "Peptide Order - Provider - Add Comment With Attachments";
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + processSuffix,
        parameters,
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const addPeptideOrderCommentManufacturer = (orderId, comment, attachments) => {
    // Manufacturer-direct path. The BE gates on session:plan ===
    // "order-manufacturer" internally; no wrapper is needed because the
    // caller IS the order's archive owner.
    const parameters = { orderId, comment };
    if (attachments && attachments.length > 0) parameters.attachments = attachments;
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Add Comment With Attachments",
        parameters,
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Reads a per-comment attachment back from the manufacturer's archive via
  // a short-lived signed URL. Routes by caller role:
  //   - isManufacturer → manufacturer-direct process (own archive)
  //   - isPatient → patient wrapper (cross-org RPC into manufacturer's archive)
  //   - else (provider) → provider wrapper (cross-org RPC into manufacturer's archive)
  // BE may throw 425 (still scanning), 410 (quarantined), 404 (not found).
  // Returns { base64, mimeType, originalName, fileName } — bytes inline
  // because `platform.bucket.signGet` is not wired on assistant-v2.
  const getPeptideOrderCommentAttachmentSignedUrl = async (orderId, fileName, { isManufacturer, isPatient } = {}) => {
    let processSuffix;
    if (isManufacturer) {
      processSuffix = "Peptide Order - Get Comment Attachment Signed URL";
    } else if (isPatient) {
      processSuffix = "Peptide Order - Patient - Get Comment Attachment Signed URL";
    } else {
      processSuffix = "Peptide Order - Provider - Get Comment Attachment Signed URL";
    }
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + processSuffix,
        parameters: { orderId, fileName },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res?.data?.output?.result;
  };

  const denyPeptideOrderManufacturer = (orderId, comment) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Manufacturer - Deny",
        parameters: { orderId, comment },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const getProviderPeptideCatalog = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide - Provider - Get Catalog",
        parameters: {},
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const output = res.data?.output || {};
    return {
      catalog: output.catalog || [],
      tierPricing: output.tierPricing || {},
      providerTiers: output.providerTiers || {},
      // Per-variant custom price overrides set by the upstream manufacturer
      // for THIS provider's org. The backend resolves the caller's own
      // OrganizationProvider edge and projects only their slice — providers
      // must never see another provider's overrides.
      // S08: the manufacturer's Peptide - Manufacturer - Get Catalog (called
      // via RPC by Peptide - Provider - Get Catalog) now projects only the
      // calling provider's slice of peptideProviderCustomPricing here.
      // Lucashealth/lucasai-backend#71.
      customPricing: output.customPricing || {},
      // S07(BE): provider's OWN patient-facing retail map { variantId: cents },
      // stored on the provider's DoctorOffice.peptidePatientPricing.
      patientPricing: output.patientPricing || {},
      // Sales-rep callers only: the CALLING rep's own per-variant cost
      // overrides ({ variantId: cents }) from salesRepCustomPricing, projected
      // by the BE for this same endpoint (reps fetch the provider catalog —
      // see usePeptidePricing). Feeds the rep's real buy price in resolvePrice
      // and the override+$1 floor in ProviderInfoDrawer.getMinPrice. This
      // return object is a whitelist — omitting a field here silently drops
      // it for every caller (that exact omission shipped the tier-instead-of-
      // override floor bug fixed 2026-07-27).
      repCustomPricing: output.repCustomPricing || {},
      // Checkout shipping methods, resolved by the manufacturer's backend from
      // peptideShippingSettings (defaults when unset). Must ride this mapper —
      // dropping it would pin the picker to the FE's mirrored defaults and
      // drift from the fee the server actually charges once ops customizes
      // the config. Empty array falls through to defaults in
      // normalizeShippingMethods.
      shippingMethods: output.shippingMethods || [],
      activeSurchargeEnabled: output.activeSurchargeEnabled !== false,
    };
  };

  const createPeptidePaymentIntent = (amountCents, orderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Provider - Create Payment Intent",
        parameters: { amountCents, orderId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  const createPeptidePaymentIntentManufacturer = (amountCents, orderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Create Payment Intent",
        parameters: { amountCents, orderId },
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // ── Peptide Orders - Patient ──
  // Dedicated patient endpoints — backend enforces patient-only scope.
  // Patient catalog strips tier pricing and provider data.
  //
  // Generate a client-side idempotency key for patient order/refill
  // mutations. Backend dedupes on this so a patient mashing Submit during
  // a network blip can't create N duplicate orders. Caller may pass an
  // explicit key to preserve idempotency across retries (e.g. when
  // Stripe.confirmPayment fails and the patient clicks Authorize again).
  // window.crypto.randomUUID is the preferred source; the time+random
  // fallback is for very old browsers that lack it (still 96+24 bits of
  // entropy — enough for FE-side dedupe).
  const makeClientIdempotencyKey = (explicitKey) => {
    if (explicitKey) return explicitKey;
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(16)}-${Math.floor(Math.random() * 0xffffffffffff)
      .toString(16)
      .padStart(12, "0")}-${Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, "0")}`;
  };

  // TODO(backend): Peptide - Patient - Get Catalog must strip tierPricing
  // to retail-only before responding. FE strips defensively in
  // usePeptidePricing.refreshPricing but backend is the real fix.
  const getPatientPeptideCatalog = async (referringProviderId) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide - Patient - Get Catalog",
        // S07 Phase 2A: the referring provider id (from the ?ref= referral) tells
        // the server which provider's retail (patientPricing) to project, so the
        // patient is shown — and charged — that provider's price. Empty → standard
        // retail (W).
        parameters: { referringProviderId: referringProviderId || "" },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return {
      catalog: output.catalog || [],
      tierPricing: output.tierPricing || {},
      patientPricing: output.patientPricing || {},
      // Manufacturer-configured checkout shipping methods — see the provider
      // mapper note; empty falls through to defaults in normalizeShippingMethods.
      shippingMethods: output.shippingMethods || [],
      activeSurchargeEnabled: output.activeSurchargeEnabled !== false,
    };
  };

  const getPatientPeptideOrders = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - List",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const getPatientPeptideOrder = (orderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Get",
        parameters: { orderId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const createPatientPeptideOrder = (orderData) => {
    // TODO(backend): implement dedupe logic keyed on clientIdempotencyKey.
    const clientIdempotencyKey = makeClientIdempotencyKey();
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Create",
        parameters: { order: orderData, clientIdempotencyKey },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const createPatientPeptideOrderWithPayment = (orderData, explicitIdempotencyKey) => {
    // TODO(backend): implement dedupe logic keyed on clientIdempotencyKey.
    // TODO(backend): re-derive totalCents from server-side catalog pricing;
    // never trust the client-supplied value (S05/S06 trust boundary).
    const clientIdempotencyKey = makeClientIdempotencyKey(explicitIdempotencyKey);
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Create With Payment",
        parameters: { order: orderData, clientIdempotencyKey },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const cancelPatientPeptideOrder = (orderId, cancelReason) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Cancel",
        parameters: { orderId, cancelReason },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const createPatientPeptidePaymentIntent = (amountCents, orderId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Create Payment Intent",
        parameters: { amountCents, orderId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const requestPatientRefill = (therapyId) => {
    // TODO(backend): implement dedupe logic keyed on clientIdempotencyKey.
    //
    // Backend response shapes the FE handles (T9):
    //   { status: "authorized" }                          → off-session re-auth on saved card succeeded
    //   { status: "card_invalid", clientSecret: "..." }   → saved card was rejected; FE must mount
    //                                                       interactive Stripe Elements with the returned
    //                                                       PaymentIntent clientSecret to collect a new card.
    const clientIdempotencyKey = makeClientIdempotencyKey();
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Request Refill",
        parameters: { therapyId, clientIdempotencyKey },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const approvePatientPlacedOrder = (orderId, { consentTimestamp, deviceMetadata, consentVersion, authMethod }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Approve",
        // authMethod ("esign" | "session-pin") rides into the server-side audit
        // record so the approval history shows HOW each order was authorized.
        parameters: { orderId, consentTimestamp, deviceMetadata, consentVersion, authMethod },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const denyPatientPlacedOrder = (orderId, reason) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Patient - Deny",
        parameters: { orderId, reason },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const getPatientPeptideConsent = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Patient Account - Get Peptide Consent",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const savePatientPeptideConsent = ({ version, locale }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Patient Account - Save Peptide Consent",
        parameters: { version, locale },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // ── Peptide Stock (Slice A + B) ──
  // Backend processes live in lucasai-backend/backend/processes/assistant-v2/.
  // Responses are role-projected server-side — providers/patients never see
  // raw onHandQty / reservedQty, only `availableQty` (when low_stock) plus a
  // coarse `status` string. Manufacturers get the full row.
  //
  // For providers and patients, stock fields are already inlined on each
  // catalog variant by the manufacturer's `Peptide - Manufacturer - Get
  // Catalog` script (RPC-proxied through the provider/patient catalog
  // endpoints). `getPeptideStock` here is primarily for manufacturer UIs
  // (CatalogManager stock column in Slice E).
  const getPeptideStock = async (skus) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Get",
        parameters: { skus: skus || [] },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return { stock: output.stock || {}, role: output.role || "provider" };
  };

  // Manufacturer-only. Server enforces role; client also defends with
  // usePeptidePricing.isManufacturer before showing the editor.
  const updatePeptideStock = (sku, payload = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Update",
        parameters: {
          sku,
          onHandQty: payload.onHandQty,
          reservedQty: payload.reservedQty,
          lowStockThreshold: payload.lowStockThreshold,
          expectedRestockDate: payload.expectedRestockDate,
          discontinued: payload.discontinued,
          reason: payload.reason,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const listPeptideStockAudit = ({ sku, limit = 50 } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Audit - List",
        parameters: { sku: sku || "", limit },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Patient/provider creates a back-in-stock subscription. Server rejects
  // manufacturer callers — they are the restock source, not the audience.
  // Idempotent: server returns the existing pending subscription if one
  // already exists for (userId, sku). Caller may pass an explicit email; if
  // omitted, the server resolves it from the session.
  const subscribePeptideBackInStock = (sku, { email } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Subscribe Back In Stock",
        parameters: { sku, email: email || "" },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const dismissPeptideBackInStock = (subscriptionId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Dismiss Back In Stock",
        parameters: { subscriptionId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Slice D — back-ordered reservation queue (manufacturer-only). The queue is
  // priority-sorted server-side. `reasons` is an array of chip strings the UI
  // surfaces next to each line: ["refill"], ["paid"], ["authorized"],
  // ["patient_continuity"], ["age:N"] (where N is days waiting).
  const getPeptideReservationQueue = async ({ sku } = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Reservation Queue - Get",
        parameters: { sku: sku || "" },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return { queue: output.queue || [], total: output.total || 0 };
  };

  // Manual override of the allocator. Server rejects if headroom can't fit qty.
  const promotePeptideReservationLine = (reservationId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Promote Line",
        parameters: { reservationId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Slice E — manufacturer stock editor helpers.
  // Each row may carry EITHER `onHandQty` (absolute set) OR `receivedQty`
  // (delta added server-side inside the transaction), not both. The optional
  // `idempotencyKey` lets a retried network failure replay safely — without
  // it, a mid-flight error would double-add the shipment on retry.
  const bulkUpdatePeptideStock = async (rows, opts = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Bulk Update",
        parameters: {
          rows: rows || [],
          idempotencyKey: opts.idempotencyKey || null,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    // Unwrap process outputs so callers can read `result.demoted` directly,
    // matching the sibling applyPeptideRestockForSkus contract. Without this,
    // BulkInventoryGrid's `saveResponse?.demoted` is always undefined and the
    // decrement chain silently no-ops.
    return res.data?.output || {};
  };

  // Dry-run for a proposed stock decrement. Returns how many active reservations
  // would flip to back_ordered. Powers the "X lines will be back-ordered" confirm.
  const previewPeptideStockDecrementImpact = ({ sku, newOnHandQty }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Decrement Impact - Get",
        parameters: { sku, newOnHandQty },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Manufacturer-only. Promotes any back-ordered reservation lines whose
  // allocated SKUs are now back in stock. Returns two lists: `promoted`
  // (reservation lines that flipped from back_ordered → reserved, each with
  // orderId / restockedAt / allocatedSkus) and `skipped` (lines that still
  // can't be fulfilled). Short-circuits to a no-op for an empty SKU list so
  // callers never fire a needless network round-trip after a no-stock event.
  const applyPeptideRestockForSkus = async ({ skus }) => {
    const cleanSkus = Array.isArray(skus) ? skus.filter(Boolean) : [];
    if (cleanSkus.length === 0) {
      return { promoted: [], skipped: [] };
    }
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Apply Restock",
        parameters: { skus: cleanSkus },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output || { promoted: [], skipped: [] };
  };

  // Manufacturer-only. Returns every variant in the org's catalog with current
  // stock, plus zero-stock placeholder rows for variants that have never been
  // saved. Powers the Bulk Inventory page.
  const getAllPeptideStock = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Get All",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return { stock: output.stock || [] };
  };

  // Manufacturer-only convenience: fetches the raw catalog payload AND the
  // current stock rows in parallel, then injects a projected `variant.stock`
  // onto each variant so the catalog grid + admin table render the same
  // OOS / low-stock badges the provider/patient views get from their RPCs
  // (which already project stock server-side). Stock failures degrade
  // gracefully — the catalog still returns, variants just lack `stock` and
  // the UI falls back to the "unknown stock" branch (renders unchanged from
  // today's behavior).
  const getManufacturerCatalogWithStock = async () => {
    const [data, stockRes] = await Promise.all([getPeptideData(), getAllPeptideStock().catch(() => ({ stock: [] }))]);
    return {
      ...data,
      catalog: mergeStockIntoCatalog(data.catalog || [], stockRes.stock || []),
    };
  };

  // Manufacturer-only. Org defaults consumed by ModalStockEditor (single-
  // product modal) and BulkInventoryGrid (bulk page) when hydrating rows
  // that have no saved stock.
  const getInventorySettings = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Inventory Settings - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return {
      defaultLowStockThreshold: output.defaultLowStockThreshold ?? 20,
      defaultRestockLeadDays: output.defaultRestockLeadDays ?? null,
    };
  };

  const saveInventorySettings = async (settings) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Inventory Settings - Save",
        parameters: {
          defaultLowStockThreshold: settings.defaultLowStockThreshold,
          defaultRestockLeadDays: settings.defaultRestockLeadDays ?? null,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return {
      defaultLowStockThreshold: output.defaultLowStockThreshold ?? settings.defaultLowStockThreshold,
      defaultRestockLeadDays: output.defaultRestockLeadDays ?? settings.defaultRestockLeadDays ?? null,
    };
  };

  // Org-level refill settings. Shape (all fields are stored and returned):
  //   refillTrackingEnabled  {boolean}     — show/hide refill surfaces (default true)
  //   peptideRefillCycleDays {number}      — days between peptide refills (default 30)
  //   dueSoonThresholdDays   {number}      — days before due that count as "due soon" (default 7)
  //   maxRefillsPerTherapy   {number|null} — cap on refills per therapy; null = unlimited (default null)
  const getRefillSettings = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Refill Settings - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return {
      refillTrackingEnabled: output.refillTrackingEnabled ?? true,
      peptideRefillCycleDays: output.peptideRefillCycleDays ?? 30,
      dueSoonThresholdDays: output.dueSoonThresholdDays ?? 7,
      maxRefillsPerTherapy: output.maxRefillsPerTherapy !== undefined ? output.maxRefillsPerTherapy : null,
    };
  };

  const saveRefillSettings = async (settings) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Refill Settings - Save",
        parameters: {
          refillTrackingEnabled: settings.refillTrackingEnabled,
          peptideRefillCycleDays: settings.peptideRefillCycleDays,
          dueSoonThresholdDays: settings.dueSoonThresholdDays,
          maxRefillsPerTherapy: settings.maxRefillsPerTherapy ?? null,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return {
      refillTrackingEnabled: output.refillTrackingEnabled ?? settings.refillTrackingEnabled,
      peptideRefillCycleDays: output.peptideRefillCycleDays ?? settings.peptideRefillCycleDays,
      dueSoonThresholdDays: output.dueSoonThresholdDays ?? settings.dueSoonThresholdDays,
      maxRefillsPerTherapy:
        output.maxRefillsPerTherapy !== undefined ? output.maxRefillsPerTherapy : (settings.maxRefillsPerTherapy ?? null),
    };
  };

  // Split-ship: ship the reserved lines whose variantIds are listed; leave the
  // rest (back-ordered or unselected reserved) for a future wave.
  const splitShipPeptideOrder = (orderId, { variantIds, carrier, trackingNumber }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Order - Split Ship",
        parameters: {
          orderId,
          variantIds: variantIds || [],
          carrier: carrier || "",
          trackingNumber: trackingNumber || "",
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Slice F — provider clinic inventory (the provider's own brain stores it).
  const getPeptideClinicInventory = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Clinic Inventory - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    const output = res.data?.output || {};
    return {
      inventory: output.inventory || {},
      anyBelowThreshold: !!output.anyBelowThreshold,
    };
  };

  const recordPeptideDispense = ({ sku, qty, patientId, reason } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Clinic Inventory - Record Dispense",
        parameters: {
          sku,
          qty: qty || 1,
          patientId: patientId || "",
          reason: reason || "",
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const recordPeptideReceipt = ({ sku, qty, orderId } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Clinic Inventory - Record Receipt",
        parameters: {
          sku,
          qty: qty || 1,
          orderId: orderId || "",
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Slice G — velocity + alert preferences.
  const getPeptideStockVelocity = async (sku) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Velocity - Get",
        parameters: { sku },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output || {};
  };

  const getPeptideStockAlertPreferences = async ({ sku } = {}) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Alert Preferences - Get",
        parameters: { sku: sku || "" },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.preferences || [];
  };

  const setPeptideStockAlertPreference = (pref) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Alert Preferences - Set",
        parameters: {
          id: pref.id || "",
          peptideSku: pref.peptideSku || "",
          alertType: pref.alertType,
          thresholdValue: pref.thresholdValue ?? 0,
          channel: pref.channel,
          enabled: pref.enabled !== false,
        },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const deletePeptideStockAlertPreference = (id) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Alert Preferences - Delete",
        parameters: { id },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Peptide notification preferences (spec 2026-07-19). `role` selects the
  // patient/provider wrapper or the manufacturer-direct canonical.
  const notifPrefsProcess = (role, verb) =>
    role === "patient"
      ? `Peptide Notification Preferences - Patient - ${verb}`
      : role === "provider"
        ? `Peptide Notification Preferences - Provider - ${verb}`
        : `Peptide Notification Preferences - ${verb}`;

  const getPeptideNotificationPrefs = async (role) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + notifPrefsProcess(role, "Get"),
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.preferences || [];
  };

  const setPeptideNotificationPrefs = async (role, preferences) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":" + notifPrefsProcess(role, "Set"),
        parameters: { preferences: JSON.stringify(preferences) },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.preferences || [];
  };

  // Slice H — in-app notification consumer. The dispatcher fires on stock
  // mutations; this returns the notifications the user hasn't seen yet (the
  // FE keeps a `lastSeen` cursor in sessionStorage so we don't reannounce on
  // every mount).
  const getPendingPeptideStockNotifications = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Peptide Stock - Pending Notifications - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.notifications || [];
  };

  // DME order notification preferences (spec 2026-07-30). Org-level rows on
  // the manufacturer brain, reached through the provider wrapper — there is no
  // manufacturer-facing screen, so no role switch here.
  const getDmeNotificationPrefs = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Notification Preferences - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.preferences || [];
  };

  const setDmeNotificationPrefs = async (preferences) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Order - Provider - Notification Preferences - Set",
        parameters: { preferences: JSON.stringify(preferences) },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.preferences || [];
  };

  // Extra notification recipients (unified settings, spec 2026-08-07 task 4).
  // Org-level, both product lines in one Get; Set replaces one product line's
  // list. Supervisor-gated server-side in the provider wrapper.
  const getNotificationRecipients = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Provider - Notification Recipients - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.recipients || { dme: [], peptide: [] };
  };

  const setNotificationRecipients = async (productLine, recipients) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Provider - Notification Recipients - Set",
        parameters: { productLine, recipients: JSON.stringify(recipients) },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.recipients || [];
  };

  // Delivery timing (unified settings, spec 2026-08-07 task 5). Org-level
  // like recipients: instant vs. daily digest plus quiet hours, one setting
  // for both product lines. Goes through the provider wrapper, which stamps
  // actorOrgId/actorUserId from this brain's session: the canonical process
  // only derives identity from the session for an order-manufacturer plan and
  // throws "Missing actorOrgId" for a provider caller.
  const getNotificationDelivery = async () => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Provider - Notification Delivery - Get",
        parameters: {},
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.delivery || null;
  };

  const setNotificationDelivery = async (delivery) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Provider - Notification Delivery - Set",
        parameters: { delivery: JSON.stringify(delivery) },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.delivery || null;
  };

  // Send-a-test button (unified settings task 6). Fans out to the provider
  // org email + extra recipients for one product line, bypassing digest and
  // quiet hours on the backend (the user is watching their inbox). Returns
  // {sent, recipients, mode} or {sent: 0, reason: "no-recipients"}.
  const sendTestNotification = async (productLine) => {
    const res = await api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Provider - Notification Test - Send",
        parameters: { productLine: productLine || "dme" },
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.output?.result || null;
  };

  const saveClinicalProfile = (profileData) => {
    const org = profileData.organizationName || profileData.organizationId || localStorage.getItem("organization");
    return api.post(
      "/api/v1/process-execute",
      {
        parameters: {
          profile: profileData.profile,
          providerId: profileData.providerId,
        },
      },
      {
        headers: {
          "x-api-process": org + ":Clinical Profile - Save",
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Provider-side entry point for the "New patient" intake-first flow. The
  // authenticated backend process owns token generation plus Twilio/email
  // delivery; the browser never creates a bearer link on its own.
  const sendClinicalProfileIntakeInvite = ({ deliveryMethod, email = "", phone = "", origin }) => {
    // The Axios instance is the request's actual destination. localStorage is
    // shared across tabs and can change underneath an older tab, so it may only
    // corroborate this value; it must never select a different tenant.
    const server = resolveClinicalProfileServerFromEndpoint(api.defaults.baseURL);
    const storedHostName = localStorage.getItem("hostName");
    const storedServer = storedHostName ? resolveClinicalProfileServerFromEndpoint(storedHostName) : null;
    if (!server) throw new TypeError("clinical_profile_server_missing");
    if (storedHostName && storedServer !== server) throw new TypeError("clinical_profile_server_mismatch");
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Send Intake Invite",
        parameters: { deliveryMethod, email, phone, origin, server },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  // Public intake links are routed to the provider org named in the URL, then
  // authorized by the purpose-specific (intakeId, intakeToken) pair. These are
  // separate from the later cpInviteToken used by the established
  // order-patient account signup/bootstrap flow.
  const verifyClinicalProfileIntakeInvite = ({ providerOrg, intakeId, intakeToken, server }) => {
    const { endpoint } = resolvePublicClinicalProfileEndpoint(server);
    return api.post(
      endpoint + "/api/v1/process-execute",
      { parameters: { intakeId, intakeToken } },
      {
        headers: {
          "x-api-process": providerOrg + ":Clinical Profile - Verify Intake Invite",
          "Content-Type": "application/json",
        },
      }
    );
  };

  const completeClinicalProfileIntakeInvite = ({ providerOrg, intakeId, intakeToken, profile, server }) => {
    const { endpoint, server: resolvedServer } = resolvePublicClinicalProfileEndpoint(server);
    return api.post(
      endpoint + "/api/v1/process-execute",
      { parameters: { intakeId, intakeToken, profile, server: resolvedServer } },
      {
        headers: {
          "x-api-process": providerOrg + ":Clinical Profile - Complete Intake Invite",
          "Content-Type": "application/json",
        },
        // The completion process is idempotent server-side, but the response
        // carries an account credential. Keep retries explicit at the caller.
        "axios-retry": { retries: 0 },
      }
    );
  };

  // Creates the ClinicalProfile chart record behind a patient who already has a
  // User account in this org. Demographics are read server-side from that
  // account, so this deliberately takes an id and nothing else. Without the
  // chart record the patient-pricing processes cannot resolve the patient.
  const createPatientClinicalProfile = (patientUserId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Provider Create For Account",
        parameters: { patientUserId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const listClinicalProfiles = ({ includeArchived = false } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - List",
        parameters: { includeArchived },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const getClinicalProfile = (profileId) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Get",
        parameters: { profileId },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const updateClinicalProfile = (profileId, { profile, demographics } = {}) => {
    const parameters = { profileId };
    if (profile !== undefined) parameters.profile = profile;
    if (demographics !== undefined) parameters.demographics = demographics;
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Update",
        parameters,
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const deleteClinicalProfile = (profileId, { force = false } = {}) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Delete",
        parameters: { profileId, force },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const promoteClinicalProfileToAccount = (profileId, { sendInviteEmail = true, regenerateToken = false, origin } = {}) => {
    // `origin` (typically `window.location.origin` from the modal) lets the
    // backend pin the invite URL to whatever environment the inviting provider
    // is on — mirrors how the provider onboarding link in
    // src/components/salesDashboard/OnboardingView.js:124 uses
    // window.location.origin directly. Backend uses it as the highest-priority
    // baseUrl, falling back to brain:baseUrl, then a hardcoded production URL.
    //
    // Gate is `typeof origin === "string" && origin.trim()` so that empty
    // strings, whitespace, null, and accidental non-string values all fall
    // through to the backend default unambiguously — sending `""` or `"  "`
    // would force the backend to defensively distinguish "FE explicitly sent
    // empty" from "FE didn't send at all," and that's two contracts for one
    // slot. Browsers always populate window.location.origin with a non-empty
    // string in our SPA mount, so the gate is defensive, not a hot path.
    const parameters = { profileId, sendInviteEmail, regenerateToken };
    if (typeof origin === "string" && origin.trim()) parameters.origin = origin;
    // The Axios instance is the request's actual destination, so its key names
    // the backend that will mint this invite. The backend prefers its own
    // brain:baseUrl and only falls back to this when that is unset, so omit it
    // rather than throw when this browser is on an unrecognized host.
    const server = resolveClinicalProfileServerFromEndpoint(api.defaults.baseURL);
    if (server) parameters.server = server;
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Promote to Account",
        parameters,
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const getMyClinicalProfile = () => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Self - Get",
        parameters: {},
      },
      // 8s timeout overrides the api default (300s) — this is a session-scoped
      // point read; if it doesn't return quickly the FE should fall through to
      // the empty-state path rather than block the patient's checkout.
      { headers: { "Content-Type": "application/json" }, timeout: 8000 }
    );
  };

  const saveMyClinicalProfile = (sectionId, data) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Self - Save",
        parameters: { sectionId, data },
      },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const bootstrapMyClinicalProfile = ({ providerOrg, profileId, inviteToken }) => {
    return api.post(
      "/web/run-process",
      {
        processId: localStorage.getItem("organization") + ":Clinical Profile - Self - Bootstrap",
        parameters: { providerOrg, profileId, inviteToken },
      },
      {
        headers: { "Content-Type": "application/json" },
        // Single-use token: do NOT retry on network errors / timeouts. The
        // global axiosRetry above will otherwise replay POST on ECONNABORTED,
        // and if the backend already consumed the token the retry returns
        // "invalid invite" — the patient sees a hard-fail toast even though
        // their profile was actually linked on the first attempt.
        "axios-retry": { retries: 0 },
      }
    );
  };

  // return functions for interface
  return {
    // Security
    login,
    logout,
    getUserProfile,
    resetPassword,
    changePassword,
    createAccount,
    getOrgStatus,
    checkOrganizationExists,
    healthCheck,
    getLinkBackoffice,
    sendLoginLink,
    checkLoginRequestStatus,
    // verifyCurrentPassword,

    // Chats
    getChats,
    getChatsFiltered,
    getChatsForVisitDays,
    getChat,
    getChatsByTempId,
    updateChat,
    updateChatRequest,
    createChat,
    createChatImport,
    deleteChat,
    changeStatusChat,
    callGenericProcess,
    unlockChat,
    generateReportCard,
    translateText,
    sendEmailAVS,
    sendSmsAVS,
    sendSms,
    sendSmsWithReminder,
    addToInsuranceGrid,

    // Session Queue
    getPatientSessions,
    resendSessionSms,
    closeSession,
    updateSessionReminders,
    getSessionQueue,
    getSessionQueueStats,
    sendSessionReminderNow,

    // Patients
    createPatient,
    listPatient,
    listDoctors,
    getEncounterAssignees,
    buildStatusFilter,
    listSalesReps,
    listMySalesTeam,
    salesReparentRep,
    salesReassignAccount,
    listSalesAuditLog,
    exportSalesAuditLogCsv,
    listSupervisors,
    listUsers,
    findPatient,
    findPatientByEmail,
    getPatient,
    updatePatient,

    // Locations
    listLocations,
    createLocation,
    getLocation,
    updateLocation,
    deleteLocation,

    // Users
    updateUser,
    getOrganizationUser,
    updateUserCustomFields,
    getUserEmail,
    createUser,
    setRepTier,
    setRepCustomPricing,
    getRepTier,
    getUserPlan,
    getStripePaymentLink,
    runUserBatch,

    // Organization
    getOrgInfo,
    updateOrgInfo,
    updateOrgCustomNotes,
    getUsedIcd10Codes,
    getSalesRep,
    getProviderDocumentStatus,
    getProviderNpiRoster,
    manuallyApproveProviderNpi,
    verifyProviderNpiUser,

    // Profile
    getProfile,
    updateProfile,
    updateProfileCustomNotes,
    verifyNpi,
    invalidateNpi,

    // Jobs
    ping,
    transcript,
    recordingBegin,
    recordingSegment,
    recordingPendingList,
    recordingGetSegments,
    recordingGetSegmentBytes,
    transcriptOnly,
    sign,
    computeEncounterData,
    updatePatientProfile,
    generate,
    createEncounterFromImage,
    importEncountersFromImage,
    generateReassestment,
    aiUpdateNote,
    aiQuickStart,
    pushNote,
    approveRequest,
    optimizeNote,
    optimizeNoteUpdateDocs,
    createPriorAuth,
    regeneateMode,
    createAfterVisitSummary,

    // Documents
    uploadDocument,
    chiroIntakeExtract,
    chiroNoteSave,
    chiroNoteGet,
    chiroNoteAppendEvent,
    logUserActivity,
    uploadDocumentWound,
    getFile,
    getMediaFile,
    deleteFile,
    editQcode,
    uploadMedia,

    // ICD 10
    getIcd10,
    getCptCodes,
    getCpt2Codes,

    // QCode,
    getQcodes,
    addQcode,
    deleteQcode,

    // SmartSheet
    getSmartSheet,
    addSmartSheet,
    deleteSmartSheet,
    editSmartSheet,

    // Reminder Templates
    getReminderTemplates,
    addReminderTemplate,
    deleteReminderTemplate,
    editReminderTemplate,

    // Reminder Scheduling
    createOrderReminder,
    cancelOrderReminder,

    // Analytics
    getStats,

    // EHR Integration
    getEhrTemplates,
    ehrProcessContent,
    saveEhrTemplate,
    reportEhrSyncAlert,
    createEhrTemplate,
    deleteEhrTemplate,

    // Order Provider
    getOrders,
    getOrder,
    getOrderProducts,
    createOrder,
    updateOrder,
    verifyInsurance,
    runInsuranceDiscovery,
    submitOrder,
    unlockSignedOrder,
    addComment,
    getOrderCommentAttachmentSignedUrl,
    uploadOrderDocument,
    uploadOrderDocumentManufacturer,
    getDocumentProvider,
    archiveOrder,

    // Order Manufacturer
    regenerateMedicalNecessity,
    getOrdersManufacturer,
    changeStatusOrder,
    addInternalNote,
    setOrderUrgent,
    getDocumentManufacturer,
    setOrderDelivered,
    updateShipmentInfo,
    updateShipTo,

    // Sales Rep Dashboard
    getOrdersStats,
    getPeptideStats,

    // DME Credentialing
    getCredentialingRequests,
    updateCredentialingRequest,
    sendCredentialingMessage,
    uploadCredentialingDocument,
    createCredentialingReminders,

    // Uber Direct
    uberDirectConnect,
    uberDirectTestConnection,
    uberDirectDisconnect,
    uberDirectCreateQuote,
    uberDirectCreateDelivery,
    uberDirectGetDelivery,
    uberDirectGetDeliveries,
    uberDirectCancelDelivery,

    // Peptide Catalog & Pricing
    getPeptideData,
    savePeptideData,
    saveCostData,
    assignProviderPricingTier,
    setProviderSurcharge,
    saveProviderCustomPricing,
    saveProviderRetailPricing,
    getOwnedProviderCustomPricing,
    unlockProviderNafEditing,
    setProviderDocExternalSignoff,
    getProviderProductRestrictions,
    setProviderProductRestrictions,
    uploadPeptideImage,
    uploadPeptidePaymentReceipt,
    getPeptideFile,

    // Peptides
    getPeptideOrders,
    getPeptideManufacturerPickupInfo,
    createPeptideOrder,
    chargePeptideOrderCard,
    chargePeptideOrderCardManufacturer,
    chargePatientPeptideOrderCard,
    getPatientPeptidePricingDetail,
    savePatientPeptidePricingDetail,
    getPatientPeptidePricingHistory,
    quotePatientPeptidePaymentLink,
    createPatientPeptidePaymentLink,
    revokePatientPeptidePaymentLink,
    getPatientPeptidePaymentLink,
    chargePatientPeptidePaymentLink,
    getPeptideOrder,
    updatePeptideOrder,
    updatePeptideOrderStatus,
    getPeptideOrdersManufacturer,
    getPeptideOrderManufacturer,
    updatePeptideOrderManufacturer,
    savePeptideInternalNote,
    updatePeptideOrderStatusManufacturer,
    createPeptideOrderManufacturer,
    addPeptideOrderComment,
    addPeptideOrderCommentManufacturer,
    getPeptideOrderCommentAttachmentSignedUrl,
    denyPeptideOrderManufacturer,
    getProviderPeptideCatalog,
    createPeptidePaymentIntent,
    createPeptidePaymentIntentManufacturer,

    // Peptides - Patient
    getPatientPeptideCatalog,
    getPatientPeptideOrders,
    getPatientPeptideOrder,
    createPatientPeptideOrder,
    createPatientPeptideOrderWithPayment,
    cancelPatientPeptideOrder,
    createPatientPeptidePaymentIntent,
    requestPatientRefill,
    approvePatientPlacedOrder,
    denyPatientPlacedOrder,
    getPatientPeptideConsent,
    savePatientPeptideConsent,

    // Peptide Stock (Slice A + B)
    getPeptideStock,
    updatePeptideStock,
    listPeptideStockAudit,
    subscribePeptideBackInStock,
    dismissPeptideBackInStock,
    // Slice D — allocator queue + manual promote
    getPeptideReservationQueue,
    promotePeptideReservationLine,
    // Slice E — stock editor helpers + split ship
    bulkUpdatePeptideStock,
    applyPeptideRestockForSkus,
    getAllPeptideStock,
    getManufacturerCatalogWithStock,
    getInventorySettings,
    saveInventorySettings,
    getRefillSettings,
    saveRefillSettings,
    previewPeptideStockDecrementImpact,
    splitShipPeptideOrder,
    // Slice F — provider clinic inventory
    getPeptideClinicInventory,
    recordPeptideDispense,
    recordPeptideReceipt,
    // Slice G — velocity + alert preferences
    getPeptideStockVelocity,
    getPeptideStockAlertPreferences,
    setPeptideStockAlertPreference,
    deletePeptideStockAlertPreference,
    // Slice G-1 — notification preferences (user settings)
    getPeptideNotificationPrefs,
    setPeptideNotificationPrefs,
    // Slice H — pending in-app stock notifications
    getPendingPeptideStockNotifications,
    getDmeNotificationPrefs,
    setDmeNotificationPrefs,
    // Extra notification recipients (unified settings, task 4)
    getNotificationRecipients,
    setNotificationRecipients,
    // Delivery timing (unified settings, task 5)
    getNotificationDelivery,
    setNotificationDelivery,
    // Send-a-test (unified settings, task 6)
    sendTestNotification,

    // Clinical Profiles
    saveClinicalProfile,
    sendClinicalProfileIntakeInvite,
    verifyClinicalProfileIntakeInvite,
    completeClinicalProfileIntakeInvite,
    createPatientClinicalProfile,
    listClinicalProfiles,
    getClinicalProfile,
    updateClinicalProfile,
    deleteClinicalProfile,
    promoteClinicalProfileToAccount,
    bootstrapMyClinicalProfile,
    getMyClinicalProfile,
    saveMyClinicalProfile,

    // Utils
    api,
  };
};

// Creates a singleton
export default createApi();
